import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { buildPipelineAssetProxyUrl, guessMediaContentType, storageKeyFromRenderableUrl } from '../../Review/services/review-media-proxy-url';
import { EditorAsset } from '../core/models/editor-asset.model';
import { EditorClip } from '../core/models/editor-clip.model';
import { EditorProject } from '../core/models/editor-project.model';
import { effectiveMotionTrackedBlurRegions, effectiveMotionTrackingKeyframes } from '../core/utils/motion-tracking.util';
import { editorMasks } from '../core/utils/editor-mask-keyframes.util';
import { clipContainsTime } from '../core/utils/timeline-math.util';
import { drawStickerPrimitive } from '../catalog/sticker-draw.util';
import { drawEffectPrimitive } from '../catalog/effect-draw.util';
import { drawTransitionPrimitive } from '../catalog/transition-draw.util';
import { drawBeatPrimitive } from '../catalog/beat-draw.util';
import { AudioEnvelope, sampleAudioEnvelope } from '../../../core/render-runtime/shared/audio-envelope.util';
import { drawAnimatedText, TextAnimSpec } from '../../../core/render-runtime/shared/text-animation.util';
import { curvedTextBounds, drawCurvedTextLine } from '../../../core/render-runtime/shared/curved-text.util';
import { resolveTextGlow, resolveTextShadow } from '../../../core/render-runtime/shared/text-effects.util';
import { resolveTextBackgroundBox } from '../../../core/render-runtime/shared/text-background.util';
import {
  paintTextBubble,
  resolveTextBubbleBox,
  type TextBubbleStyle,
} from '../../../core/render-runtime/shared/text-bubble.util';
import {
  ensureEditorFontInDocument,
  ensureTextDesignFontsInDocument,
} from '../../../core/render-runtime/worker/render-font-assets';
import { adjustFilterCss, adjustIsActive, adjustHasOverlay, filterEffectAdjust, paintAdjustOverlays } from '../../../core/render-runtime/shared/media-adjust.util';
import { resolveAdjustAt } from '../../../core/render-runtime/shared/media-adjust-keyframes.util';
import { applyBlurRegions, blurRegionsActive } from '../../../core/render-runtime/shared/media-blur-region.util';
import { applyMaskClip, paintMaskAlpha, resolveMaskAt, type ResolvedMask } from '../../../core/render-runtime/shared/media-mask.util';
import { applyChromaKey } from '../../../core/render-runtime/shared/media-chroma.util';
import { mediaBlendOperation, mediaEnhancementFilterCss, mediaMotionBlurAlpha, mediaStabilizationOffset, mediaStabilizationScale, mediaTrackingBackgroundBlur } from '../../../core/render-runtime/shared/media-enhancements.util';
import { speedSourceTime, speedRateAt } from '../../../core/render-runtime/shared/media-speed.util';
import { MediaCacheService } from './media-cache.service';
import { resolvePreviewSourceAudioVolumeMulti } from './preview-source-audio.util';
import { PreviewVideoSyncSnapshot, shouldSeekPreviewVideoElement } from './preview-video-sync-state.util';
import { MediabunnyFrameSourceService, MEDIABUNNY_FALLBACK, PreviewDrawable } from './mediabunny-frame-source.service';
import { PreviewAudioCleanupService } from './preview-audio-cleanup.service';
import { CombinedMotionDelta, MotionClipSpec, composeMotionDelta, resolveTransformAt } from '../../../core/render-runtime/shared/transform-keyframes.util';
import { animationZoom, motionOffsetNorm } from '../../../core/render-runtime/shared/enter-exit-presets';
import { getGlEffectRunner, GlEffectRunnerDef } from '../../../core/render-runtime/worker/webgl/gl-effect-runner';
import { GL_EFFECTS } from '../../../core/render-runtime/worker/webgl/gl-effects.source';
import {
  isEffectSceneProgram,
  type EffectSceneProgram,
} from '../../../core/render-runtime/shared/effect-scene-program';
import { getGlTransitionRunner, GlTransitionRunnerDef } from '../../../core/render-runtime/worker/webgl/gl-transition-runner';
import { GL_TRANSITIONS } from '../../../core/render-runtime/worker/webgl/gl-transitions.source';
import {
  TransitionBlendPlan,
  TransitionNeighborClip,
  crossfadeFromAlpha,
  crossfadeToAlpha,
  resolveTransitionBlendPlan,
  resolveTransitionNeighbors,
  slideIncomingOffset,
  slideOutgoingOffset,
  wipeRevealRect,
} from '../../../core/render-runtime/shared/transition-blend.util';
import {
  activeDrawingLayers,
  compositeDrawingSurface,
  renderDrawingLayers,
} from '../../../core/render-runtime/shared/drawing-renderer.util';
import { drawTextDesignPreset } from '../../../core/render-runtime/shared/text-design-renderer.util';
import { resolveTextDesignSelectionBounds } from './text-design-selection-bounds.util';
import {
  shouldDrawTextDesignInPreview,
  shouldRevealSelectedTextDesignAtStart,
} from './text-design-authoring-preview.util';
import { resolveEditorTextDesignText } from '../core/utils/text-design-text.util';
import { withoutUnwantedTextDesignSurface } from '../core/utils/text-design-surface.util';
import { resolveEditorTextStyleAt } from '../core/utils/editor-text-style-keyframes.util';
import { HumanVisionService } from '../vision/human-vision.service';
import {
  HumanVisionResult,
  humanVisionResultSatisfiesTarget,
  humanVisionTargetForEffect,
  requiredHumanVisionTarget,
} from '../vision/human-vision.protocol';
import {
  isTrustedBodyRuntimeId,
  isTrustedEffectPrimitive,
} from '../../../core/render-runtime/trusted-runtime-capabilities';
import { CatalogCacheService } from '../catalog-engine/catalog-cache.service';
import {
  effectSceneCanvasLayout,
  type EffectSceneCanvasLayout,
} from '../../../core/render-runtime/shared/effect-scene-layout.util';

export interface WebAvCanvasPort {
  previewFrame(project: EditorProject, timeSeconds: number, canvas: HTMLCanvasElement, options?: CanvasPreviewOptions): Promise<void>;
  drawFrame(project: EditorProject, timeSeconds: number, canvas: HTMLCanvasElement, options?: CanvasPreviewOptions): Promise<void>;
}

export interface CanvasPreviewOptions {
  isPlaying?: boolean;
  playbackRate?: number;
  /** Selected clip in the editor. Used only for paused authoring affordances. */
  selectedClipId?: string | null;
  allowTaintedPreview?: boolean;
  /** @deprecated single-clip audio gate — kept for callers/tests; superseded by audibleSourceClipIds. */
  audibleSourceClipId?: string | null;
  /**
   * Every source-video clip that should be AUDIBLE this frame (A4: preview mixes all
   * active non-muted source clips, like the worker). When provided it takes precedence
   * over `audibleSourceClipId`. Omitted → falls back to the single-id gate.
   */
  audibleSourceClipIds?: ReadonlySet<string> | null;
}

/** A coalesced preview-draw request held by the supersede queue (newest wins). */
interface PendingDraw {
  project: EditorProject;
  timeSeconds: number;
  options: CanvasPreviewOptions;
}

interface ResolvedCanvasClipTransform {
  offsetX: number;
  offsetY: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
}

export interface PreviewMediaClock {
  projectId: string;
  clipId: string;
  timelineTimeSeconds: number;
  sourceTimeSeconds: number;
  mediaDurationSeconds: number | null;
  paused: boolean;
  generatedAtMs: number;
}

/** A decoded transition-neighbor frame (video drawable or a loaded image) + a release hook. */
type NeighborFrame =
  | { kind: 'video'; drawable: PreviewDrawable; close: () => void }
  | { kind: 'image'; image: HTMLImageElement; close: () => void };

@Injectable({ providedIn: 'root' })
export class WebavCanvasService implements WebAvCanvasPort {
  private drawingCompositeSurface: {
    width: number;
    height: number;
    layers: readonly object[];
    canvas: HTMLCanvasElement;
  } | null = null;
  private readonly imageCache = new Map<string, Promise<HTMLImageElement>>();
  /** Durable scene URL -> decoded image. Populated only when its effect becomes active. */
  private readonly effectSceneImages = new Map<string, Promise<HTMLImageElement | null>>();
  private readonly resolvedEffectSceneImages = new Map<string, HTMLImageElement>();
  private readonly videoCache = new Map<string, Promise<HTMLVideoElement>>();
  private readonly videoReadyKeys = new Set<string>();
  private readonly frameInFlight = new Map<HTMLCanvasElement, Promise<void>>();
  /**
   * Supersede queue (VideoFlow `renderFrame` pattern). Drawing a video frame is now
   * async (await the mediabunny decode), so a fast scrub can queue many draws. We
   * coalesce: while a draw for a canvas is in flight we only remember the LATEST
   * requested params; when the in-flight draw finishes we render that newest one and
   * drop everything in between. This is what makes dragging the playhead smooth.
   */
  private readonly pendingDraw = new Map<HTMLCanvasElement, PendingDraw>();
  /** Per-canvas off-screen double-buffer — compose here, then blit atomically so the
   *  visible canvas never shows a half-drawn / cleared (black) frame while scrubbing.
   *  Not readonly: `disposeAll()` swaps in a fresh map to drop the backbuffer canvases. */
  private backbuffers = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
  private effectSceneSourceScratch: HTMLCanvasElement | null = null;
  private effectSceneCompositeScratch: HTMLCanvasElement | null = null;
  private readonly videoSeekQueue = new WeakMap<HTMLVideoElement, Promise<void>>();
  /** Per-element source-progress tracking for the stalled-master watchdog (see buildPreviewMediaClock). */
  private readonly videoProgressWatch = new WeakMap<HTMLVideoElement, { sourceTime: number; changedAtMs: number }>();
  private readonly videoPlaybackState = new WeakMap<HTMLVideoElement, PreviewVideoSyncSnapshot>();
  private readonly videoDesiredVolume = new WeakMap<HTMLVideoElement, number>();
  private readonly previewVideos = new Set<HTMLVideoElement>();
  private previewMediaClock: PreviewMediaClock | null = null;
  private previewScrubbing = false;
  /** When a user scrubs/seeks while playing, the target timeline time the clock holds at until
   *  the <video> finishes seeking there (prevents playhead/video oscillation during the seek). */
  private pendingSeekTimelineTime: number | null = null;
  private videoHost: HTMLElement | null = null;
  private readonly maxImageCacheEntries = 32;
  private readonly maxVideoCacheEntries = 8;
  /** storageKey → object-URL of the proxied media blob (shared by preview draws + duration probe). */
  private readonly blobUrlCache = new Map<string, Promise<string>>();
  /** raw clip URL → the URL handed to the mediabunny decoder (so isSourceReady can match the key). */
  private readonly lastDecodeUrlFor = new Map<string, string>();
  /**
   * Combined-timeline audio reactivity curve (bass/mid/treble/amplitude per frame) that
   * beat overlays pulse to, so the PREVIEW reacts to the real sound. Set by the editor
   * after Detect Beats builds the mixdown; null → beats fall back to the natural loop.
   */
  private audioEnvelope: AudioEnvelope | null = null;

  /** Supply (or clear) the beat-reactivity envelope the preview samples per frame. */
  setAudioEnvelope(envelope: AudioEnvelope | null): void {
    this.audioEnvelope = envelope;
  }

  constructor(
    private readonly mediaCache: MediaCacheService,
    private readonly mediabunny: MediabunnyFrameSourceService,
    private readonly previewCleanup: PreviewAudioCleanupService,
    private readonly humanVision: HumanVisionService,
    private readonly catalogCache: CatalogCacheService = new CatalogCacheService(),
  ) {}

  /**
   * Release every decoder + cached resource this service holds. Call from the editor's
   * `ngOnDestroy` so closing the editor frees the WebCodecs `VideoDecoder`s (a real GPU/
   * memory leak otherwise — each `DecodedSource` keeps a hardware decoder open).
   *
   * Forwards to `MediabunnyFrameSourceService.disposeAll()` (closes all `DecodedSource`
   * decoders + their LRU sample caches), then drops this service's own caches: decoded
   * images, the off-DOM <video> elements (paused + detached so their media pipelines
   * stop), the readiness/decode-url bookkeeping, the proxy blob-URL cache, and the
   * per-canvas backbuffers. Idempotent and safe to call when nothing was ever drawn.
   */
  disposeAll(): void {
    // 1. WebCodecs decoders (the heavy native handles).
    try { this.mediabunny.disposeAll(); } catch { /* best-effort teardown */ }

    // 2. Off-DOM <video> elements — pause, detach src, remove from the DOM host.
    for (const videoPromise of this.videoCache.values()) {
      void videoPromise.then(video => this.releaseVideo(video)).catch(() => undefined);
    }

    // 3. Drop all in-memory caches / bookkeeping.
    this.imageCache.clear();
    this.effectSceneImages.clear();
    this.resolvedEffectSceneImages.clear();
    this.videoCache.clear();
    this.videoReadyKeys.clear();
    this.blobUrlCache.clear();
    this.lastDecodeUrlFor.clear();
    this.previewVideos.clear();
    this.pendingDraw.clear();
    this.previewMediaClock = null;
    this.previewScrubbing = false;
    this.drawingCompositeSurface = null;
    this.humanVision.reset();

    // 4. Drop the per-canvas backbuffers (WeakMap → swap for a fresh empty one).
    this.backbuffers = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
    this.effectSceneSourceScratch = null;
    this.effectSceneCompositeScratch = null;

    // 5. The authenticated preview/export cache owns the session copies of remote
    // media (Blob + object URL). Once the editor is gone, keeping those bytes would
    // make the next session look warm while leaking the previous session's media.
    // A command that is still running has already received its structured-cloned
    // Blobs in the worker; completed/cancelled commands clear this cache as well.
    try { this.mediaCache.clearAll(); } catch { /* best-effort teardown */ }
  }

  /**
   * Cached theme colors keyed by the active data-theme. drawFrame runs every animation frame,
   * and getComputedStyle(documentElement)+getPropertyValue forces a style recalc each call —
   * doing it ~60x/s was a real source of preview jank/stutter. We recompute only when the
   * theme actually changes (rare), otherwise return the cached values.
   */
  private themeColorsCache: { key: string; background: string; text: string } | null = null;
  private resolveThemeColors(): { background: string; text: string } {
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    const key = `${root?.getAttribute('data-theme') ?? ''}|${(typeof document !== 'undefined' ? document.body?.getAttribute('data-theme') : '') ?? ''}`;
    const cached = this.themeColorsCache;
    if (cached && cached.key === key) return cached;
    const theme = getComputedStyle(document.documentElement);
    const background = theme.getPropertyValue('--editor-stage-bg').trim() || theme.getPropertyValue('--bg').trim();
    const text = theme.getPropertyValue('--text').trim() || 'currentColor';
    this.themeColorsCache = { key, background, text };
    return this.themeColorsCache;
  }

  async previewFrame(
    project: EditorProject,
    timeSeconds: number,
    canvas: HTMLCanvasElement,
    options: CanvasPreviewOptions = {}
  ): Promise<void> {
    // Coalesce concurrent requests (supersede queue). If a draw is already running
    // for this canvas, just record the newest target and return — when the running
    // draw finishes it picks up the latest pending params and renders ONLY those, so
    // intermediate scrub positions are dropped and dragging stays smooth.
    if (this.frameInFlight.has(canvas)) {
      this.pendingDraw.set(canvas, { project, timeSeconds, options });
      return this.frameInFlight.get(canvas);
    }
    return this.runDrawLoop(canvas, { project, timeSeconds, options });
  }

  /** Runs a draw, then drains any newest pending request that arrived while it ran. */
  private runDrawLoop(canvas: HTMLCanvasElement, first: PendingDraw): Promise<void> {
    const loop = (async () => {
      let next: PendingDraw | undefined = first;
      while (next) {
        const { project, timeSeconds, options } = next;
        try {
          await this.drawFrame(project, timeSeconds, canvas, options);
        } catch {
          /* keep the loop alive so a later good frame still renders */
        }
        next = this.pendingDraw.get(canvas);
        this.pendingDraw.delete(canvas);
      }
    })().finally(() => this.frameInFlight.delete(canvas));
    this.frameInFlight.set(canvas, loop);
    return loop;
  }

  async drawFrame(
    project: EditorProject,
    timeSeconds: number,
    canvas: HTMLCanvasElement,
    options: CanvasPreviewOptions = {}
  ): Promise<void> {
    const visibleContext = canvas.getContext('2d');
    if (!visibleContext) return;
    // Compose into an off-screen back-buffer, then blit it onto the visible canvas in
    // ONE shot at the very end. The visible canvas keeps showing the PREVIOUS frame the
    // whole time we clear → decode → composite the new one, so clicking the timeline
    // never flashes the background while the frame loads (the "black screen" symptom).
    // Combined with the keyframe-seek decode (worker-decode) + the supersede queue,
    // this is the standard double-buffer every real-time NLE preview uses.
    const backbuffer = this.getBackbuffer(canvas);
    const context = backbuffer.getContext('2d');
    if (!context) return;
    const { background, text } = this.resolveThemeColors();
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // --- Visual pass: active video/image clips in the user's track-stack order ---
    // Draw every active visual clip through the SAME
    // drawAsset/withClipTransform path so geometry (normalized rect + cover fit)
    // matches export. A single-video timeline yields exactly one iteration → identical
    // output to the old single-topmost pass.
    const orderedClips = this.activeVisualClipsOrdered(project, timeSeconds);
    const activeVideoUrls = new Set<string>();
    const activeVideoElements = new Set<HTMLVideoElement>();
    // Per-frame de-dup: the FIRST active clip of a given source URL streams through the
    // shared mediabunny reader (getDrawableAt); each ADDITIONAL same-URL clip (duplicate
    // / same-source PiP) must own its own seeked frame (getOwnedDrawableAt) or it would
    // advance the shared reader and corrupt the first clip's frame this same frame —
    // the preview-side mirror of the A1 render bug. Images share one bitmap (no conflict).
    const seenSourceDecodeUrls = new Set<string>();
    // Master clock comes from the TOPMOST active source-video clip on a BASE video lane
    // (the last base-lane video in z-order). Overlay PiPs are excluded — their clip window
    // covers only a slice of the timeline, so letting one drive would clamp/freeze the
    // playhead at ITS end instead of playing on to the end of all elements.
    const topmostVideoClipId = this.topmostActiveVideoClipId(project, orderedClips);
    // MOTION clips (kind:'motion' on 'animation' tracks) animate the BASE visual frame.
    // Compose every motion active at this time into ONE combined delta (via the SHARED
    // composeMotionDelta — the SAME the worker uses) and fold it onto the BASE visual
    // clip's transform. "Base" = the TOPMOST active visual (the LAST clip in this
    // bottom→top list, the one the frame is built around). No active motion ⇒ identity ⇒
    // every clip draws byte-for-byte as today (the fast path is preserved).
    const motionDelta = composeMotionDelta(this.activeMotionClips(project, timeSeconds), timeSeconds);
    const baseVisualClipId = orderedClips.length ? orderedClips[orderedClips.length - 1].id : null;
    let mediaClock: PreviewMediaClock | null = null;
    const drawnTextTrackIds = new Set<string>();
    for (const clip of orderedClips) {
      const trackIndex = project.tracks.findIndex(track => track.id === clip.trackId);
      // Caption tracks that are lower than this media track must paint before it.
      // This makes the visible layer stack authoritative instead of forcing text on top.
      for (let index = 0; index < trackIndex; index++) {
        const track = project.tracks[index];
        if (drawnTextTrackIds.has(track.id) || (track.kind !== 'caption' && track.kind !== 'effect')) continue;
        this.drawTextClips(context, project, timeSeconds, text, track.id);
        drawnTextTrackIds.add(track.id);
      }
      const asset = project.assets.find(item => item.id === clip.assetId);
      if (!asset) continue;
      const isVideo = asset.kind === 'video' || clip.kind === 'video';
      const url = asset.objectUrl || asset.sourceUrl;
      if (url && isVideo) {
        activeVideoUrls.add(this.videoCacheKey(url, Boolean(options.allowTaintedPreview)));
      }
      const result = await this.drawAsset(
        context,
        canvas,
        project.id,
        asset,
        clip,
        timeSeconds,
        options,
        activeVideoElements,
         seenSourceDecodeUrls,
         clip.id === topmostVideoClipId,
         clip.id === baseVisualClipId ? motionDelta : undefined,
         project.tracks.some(track => track.id === clip.trackId && track.kind === 'overlay'),
       );
      // Only the topmost video clip's clock drives the timeline (others still play
      // their audio, but must not fight the master clock).
      if (result && clip.id === topmostVideoClipId) mediaClock = result;
    }

    for (const track of project.tracks) {
      if (drawnTextTrackIds.has(track.id) || (track.kind !== 'caption' && track.kind !== 'effect')) continue;
      this.drawTextClips(context, project, timeSeconds, text, track.id);
    }

    this.pauseInactiveVideos(activeVideoUrls, activeVideoElements, Boolean(options.isPlaying));

    // CANONICAL Z-ORDER (bottom → top), matched EXACTLY by the worker compositor
    // (worker-compositor `composeFrame`) so overlapping layers stack identically in
    // preview and export:
    //   media clips → transitions → effects → beats → stickers → text/captions (TOPMOST).
    // Transitions composite over the visuals first; captions/text sit on top so they are
    // always readable. A single-layer project is unaffected (each empty pass is a no-op).
    //
    // Transitions: TRUE A↔B blends (crossfade/wipe/slide/push) composite the outgoing and
    // incoming clip frames over the already-drawn visuals; stylistic primitives keep their
    // color overlay. Async because a blend decodes both neighbor frames; awaited so the
    // atomic present below shows the completed blend (never a half-composited frame).
    const visionTarget = requiredHumanVisionTarget(
      this.clipsFromVisibleTracks(project)
        .filter(clip => clip.kind === 'effect' && !!clip.effect)
        .map(clip => ({
          startSeconds: clip.startSeconds,
          endSeconds: clip.endSeconds,
          params: clip.effect as unknown as Record<string, unknown>,
        })),
      timeSeconds,
    );
    this.humanVision.requestDetection(context.canvas, Math.round(timeSeconds * 1000), visionTarget);
    const vision = this.humanVision.latestFor(visionTarget);

    this.drawTimedFilterClips(context, project, timeSeconds);
    await this.drawTransitionClips(context, project, timeSeconds, options);
    await this.prepareActiveEffectSceneImages(project, timeSeconds);
    this.drawEffectClips(context, project, timeSeconds, vision);
    this.drawBeatClips(context, project, timeSeconds);
    this.drawStickerClips(context, project, timeSeconds);
    this.drawDrawingClips(context, project, timeSeconds);
    this.drawTextDesignClips(context, project, timeSeconds, options);

    // Atomic present: swap in the freshly-composed frame only now that it's complete.
    visibleContext.drawImage(backbuffer, 0, 0);

    // Publish the clock only when it still owns the newest requested position. A draw can
    // take long enough (especially around true A↔B transitions) for a request from the next
    // clip to supersede it. Publishing the outgoing clip's clock used to pull the store back
    // over the cut and create a seek/pause feedback loop. Requests inside the SAME clip may
    // still publish so a continuously queued playback draw cannot starve the media clock.
    if (this.canPublishPreviewMediaClock(canvas, project, mediaClock)) {
      this.previewMediaClock = mediaClock;
    }
  }

  private canPublishPreviewMediaClock(
    canvas: HTMLCanvasElement,
    project: EditorProject,
    mediaClock: PreviewMediaClock | null
  ): boolean {
    const pending = this.pendingDraw.get(canvas);
    if (!pending) return true;
    if (pending.project.id !== project.id) return false;
    const pendingClockClipId = this.topmostActiveVideoClipId(
      pending.project,
      this.activeVisualClipsOrdered(pending.project, pending.timeSeconds)
    );
    return pendingClockClipId === (mediaClock?.clipId ?? null);
  }

  /** Per-canvas off-screen double-buffer, kept sized to its visible canvas. */
  private getBackbuffer(canvas: HTMLCanvasElement): HTMLCanvasElement {
    let buffer = this.backbuffers.get(canvas);
    if (!buffer) {
      buffer = document.createElement('canvas');
      this.backbuffers.set(canvas, buffer);
    }
    if (buffer.width !== canvas.width) buffer.width = canvas.width;
    if (buffer.height !== canvas.height) buffer.height = canvas.height;
    return buffer;
  }

  /**
   * Flat list of clips drawn in preview, EXCLUDING clips on `hidden` tracks (a hidden track is
   * omitted from the output entirely — matching the render adapter). All non-media draw passes
   * (beat/effect/sticker/text/transition) gather through this so hidden tracks vanish uniformly.
   */
  private clipsFromVisibleTracks(project: EditorProject): EditorClip[] {
    const clips: EditorClip[] = [];
    for (const track of project.tracks) {
      if (track.hidden) continue;
      for (const clip of track.clips) clips.push(clip);
    }
    return clips;
  }

  private drawBeatClips(context: CanvasRenderingContext2D, project: EditorProject, timeSeconds: number): void {
    const beatClips = this.clipsFromVisibleTracks(project)
      .filter(clip => this.isActive(clip, timeSeconds) && clip.kind === 'beat' && !!clip.beat);

    // Sample the combined-timeline audio ONCE for this frame — every active beat reacts to
    // the same real sound. NO envelope (no audio / not detected) → pass null so beats fall
    // back to their VISIBLE natural loop (sampling a null envelope yields a truthy all-zero
    // reading, which would otherwise force the reactive path to draw nothing).
    const audio = this.audioEnvelope ? sampleAudioEnvelope(this.audioEnvelope, timeSeconds) : null;
    for (const clip of beatClips) {
      const params = clip.beat;
      if (!params) continue;
      const duration = Math.max(0.05, clip.endSeconds - clip.startSeconds);
      const elapsed = Math.max(0, timeSeconds - clip.startSeconds);
      const progress = Math.min(1, elapsed / duration);
      const opacity = Math.min(1, Math.max(0, params.opacity ?? 0.85));
      drawBeatPrimitive(context, params, opacity, context.canvas.width, context.canvas.height, elapsed, progress, audio);
    }
  }

  /**
   * Every ACTIVE visual clip (video/image) across the 'video' and 'overlay' tracks,
   * in the EXACT draw order the render worker uses (render.worker.ts `sortVisualTracks`
   * + its gather loop): all 'video' tracks first (bottom), then all 'overlay' tracks
   * (top); clips kept in array order within each track (z-order ascending). Drawing
   * this whole set — instead of only the topmost clip — is what makes overlay/PiP
   * videos and multi-track layouts appear in preview exactly as they export.
   *
   * A single-video timeline returns a one-element array, so the draw path is unchanged.
   */
  private activeVisualClipsOrdered(project: EditorProject, timeSeconds: number): EditorClip[] {
    const ordered: EditorClip[] = [];
    for (const track of project.tracks) {
      if (track.kind !== 'video' && track.kind !== 'overlay') continue;
      // HIDDEN track ⇒ its visual clips are skipped in the preview (mirrors the render adapter,
      // which omits a hidden track's clips from the job).
      if (track.hidden) continue;
      for (const clip of track.clips) {
        if (!clip.assetId) continue;
        if (clip.kind !== 'video' && clip.kind !== 'image') continue;
        if (!this.isActive(clip, timeSeconds)) continue;
        ordered.push(clip);
      }
    }
    return ordered;
  }

  /**
   * The id of the topmost active SOURCE-VIDEO clip on a BASE ('video'-kind) track in an
   * ordered (bottom→top) list. Used to pick the single master-clock clip while every
   * active video still plays its own audio. Overlay-track videos (PiP recordings etc.)
   * never qualify — their short window must not drive (and clamp) the whole timeline.
   *
   * RECORDINGS are also excluded (see `isRecordedAsset`): a MediaRecorder WebM reports
   * duration = Infinity and can freeze its `currentTime` without firing 'ended', so as a
   * master it pins the playhead at its start ("plays to the recording, then sticks and
   * never restarts"). With no eligible master, the wall-clock fallback advances the
   * playhead through — and wraps at the end of — the recording's window; the recording
   * still plays + draws (audio/visual) as a non-master clip.
   */
  private topmostActiveVideoClipId(project: EditorProject, orderedClips: EditorClip[]): string | null {
    const baseVideoTrackIds = new Set(
      project.tracks.filter(track => track.kind === 'video' && !track.hidden).map(track => track.id)
    );
    for (let i = orderedClips.length - 1; i >= 0; i--) {
      const clip = orderedClips[i];
      if (clip.kind !== 'video' || !baseVideoTrackIds.has(clip.trackId)) continue;
      if (this.isRecordedAsset(project, clip.assetId)) continue;
      return clip.id;
    }
    return null;
  }

  /** A clip backed by an in-editor recording (camera/screen capture) — its source is an
   *  Infinity-duration MediaRecorder WebM that must not drive the timeline clock. */
  private isRecordedAsset(project: EditorProject, assetId: string | null | undefined): boolean {
    if (!assetId) return false;
    return project.assets.find(a => a.id === assetId)?.metadata?.['recorded'] === true;
  }

  /**
   * Every ACTIVE MOTION clip (kind:'motion' on an 'animation' track) at this time, as the
   * minimal {start,end,keyframes} shape `composeMotionDelta` consumes — the SAME data the
   * render adapter collects into `timeline.motions`. Hidden tracks are skipped (mirrors the
   * adapter + preview's other passes). Empty when no motion clip overlaps, so the
   * compositor's fast path (zero motion ⇒ base unchanged) is preserved.
   */
  private activeMotionClips(project: EditorProject, timeSeconds: number): MotionClipSpec[] {
    const out: MotionClipSpec[] = [];
    for (const track of project.tracks) {
      if (track.hidden) continue;
      if (track.kind !== 'animation') continue;
      for (const clip of track.clips) {
        if (clip.kind !== 'motion') continue;
        if (!this.isActive(clip, timeSeconds)) continue;
        if (!clip.keyframes || !clip.keyframes.length) continue;
        out.push({ startSeconds: clip.startSeconds, endSeconds: clip.endSeconds, keyframes: clip.keyframes });
      }
    }
    return out;
  }

  private drawDrawingClips(context: CanvasRenderingContext2D, project: EditorProject, timeSeconds: number): void {
    const drawings = this.clipsFromVisibleTracks(project)
      .filter(clip => clip.kind === 'drawing' && !!clip.drawing);
    const layers = activeDrawingLayers(drawings.map(clip => ({
      startSeconds: clip.startSeconds,
      endSeconds: clip.endSeconds,
      layer: clip.drawing ?? null,
    })), timeSeconds);
    if (!layers.length) return;

    const width = context.canvas.width;
    const height = context.canvas.height;
    let cached = this.drawingCompositeSurface;
    const sameLayers = cached
      && cached.width === width
      && cached.height === height
      && cached.layers.length === layers.length
      && cached.layers.every((layer, index) => layer === layers[index]);

    if (!cached || cached.width !== width || cached.height !== height) {
      const surface = document.createElement('canvas');
      surface.width = width;
      surface.height = height;
      cached = { width, height, layers: [], canvas: surface };
    }

    if (!sameLayers) {
      const layerContext = cached.canvas.getContext('2d');
      if (!layerContext) return;
      renderDrawingLayers(layerContext, layers, { width, height });
      cached.layers = [...layers];
      this.drawingCompositeSurface = cached;
    }

    // Drawings are authored in composition coordinates. They intentionally ignore
    // the generic whole-clip transform: Draw mode selects/edits strokes directly.
    compositeDrawingSurface(context, cached.canvas, { width, height });
  }

  private drawTextDesignClips(
    context: CanvasRenderingContext2D,
    project: EditorProject,
    timeSeconds: number,
    options: CanvasPreviewOptions,
  ): void {
    const designs = this.clipsFromVisibleTracks(project)
      .filter(clip =>
        clip.kind === 'text'
        && !!clip.textDesign
        && shouldDrawTextDesignInPreview(
          clip,
          timeSeconds,
          Boolean(options.isPlaying),
          options.selectedClipId,
        )
      );
    const width = context.canvas.width;
    const height = context.canvas.height;

    for (const clip of designs) {
      const preset = clip.textDesign;
      if (!preset) continue;
      const visiblePreset = withoutUnwantedTextDesignSurface(preset);
      const resolvedText = resolveEditorTextStyleAt(
        clip.color,
        clip.textStyle,
        clip.textStyleKeyframes,
        timeSeconds - clip.startSeconds,
      );
      // Includes the effective primary inspector override (family + language), not
      // only the preset layers, so live preview resolves the same faces as export.
      void ensureTextDesignFontsInDocument(visiblePreset, clip.textStyle);
      const authoringClip = shouldRevealSelectedTextDesignAtStart(
        clip,
        timeSeconds,
        Boolean(options.isPlaying),
        options.selectedClipId,
      )
        ? { ...clip, animateIn: null }
        : clip;
      this.withClipTransform(context, authoringClip, width / 2, height / 2, timeSeconds, () => {
        drawTextDesignPreset(context, visiblePreset, {
          width,
          height,
          originX: -width / 2,
          originY: -height / 2,
          text: resolveEditorTextDesignText(clip),
          primaryStyle: { ...resolvedText.textStyle },
          primaryColor: resolvedText.color || undefined,
        });
      });
    }
  }

  private drawTextClips(context: CanvasRenderingContext2D, project: EditorProject, timeSeconds: number, fallbackColor: string, trackId?: string): void {
    const textClips = project.tracks
      .filter(track => !track.hidden && (track.kind === 'caption' || track.kind === 'effect') && (!trackId || track.id === trackId))
      .flatMap(track => track.clips)
      .filter(clip => this.isActive(clip, timeSeconds) && !clip.textDesign && !!(clip.text || clip.title) && (clip.kind === 'caption' || clip.kind === 'text'));

    for (const clip of textClips) {
      const content = String(clip.text || clip.title || '').trim();
      if (!content) continue;
      const resolvedText = resolveEditorTextStyleAt(
        clip.color,
        clip.textStyle,
        clip.textStyleKeyframes,
        timeSeconds - clip.startSeconds,
      );
      const ts = resolvedText.textStyle;

      // FONT SIZE — single source of truth = the 1080-wide REFERENCE px the adapter
      // pins on RenderCaption.fontSizePx, which is `fontSize × sizePercent`. Both the
      // preview (here) and the worker compute the on-screen px as that reference ×
      // (outputWidth / 1080), so a caption is the SAME size in the editor and the
      // export at ANY resolution. (Previously the preview omitted sizePercent and the
      // worker omitted the width scale, so only the 1080-wide case happened to match.)
      const referencePx = (ts.fontSize ?? 48) * this.positiveOr(ts.sizePercent, 1);
      const fontPx = Math.max(12, Math.round(referencePx * (context.canvas.width / 1080)));

      // Numeric weight when authored (800 extra-bold presets); else 700 — mirrors the worker.
      const weight = typeof ts.fontWeight === 'number' ? ts.fontWeight : 700;
      const family = this.captionFontFamily(ts);
      const align = (ts.align ?? 'center') as CanvasTextAlign;
      const color = resolvedText.color || fallbackColor || '#ffffff';
      const background = (ts.background || '').trim() || null;
      // Authored caption styling (Text-tool presets) — same fallbacks as the worker:
      // bg-opacity absent ⇒ 1; stroke undefined ⇒ default dark, null ⇒ none, color ⇒ that.
      const backgroundOpacity = typeof ts.backgroundOpacity === 'number' ? ts.backgroundOpacity : 1;
      const strokeColor = ts.stroke === undefined ? 'rgba(0, 0, 0, 0.62)' : ts.stroke;
      const strokeWidthRef = typeof ts.strokeWidth === 'number' ? ts.strokeWidth : null;
      const baseY = clip.transform ? context.canvas.height / 2 : context.canvas.height * 0.78;
      const capT = timeSeconds - clip.startSeconds;
      this.withClipTransform(context, clip, context.canvas.width / 2, baseY, timeSeconds, () => {
        // Draw around (0,0): withClipTransform has already translated the origin to the
        // caption anchor, exactly as the worker draws around (c.x·outW, c.y·outH).
        this.drawCaptionBody(context, content, fontPx, weight, family, align, color, background, backgroundOpacity, strokeColor, strokeWidthRef, {
          gradient: ts.gradient ?? null,
          glow: ts.glow ?? null,
          shadow: ts.shadow ?? null,
          shadow3d: ts.shadow3d ?? null,
          bubble: ts.bubble ?? null,
          textAnim: ts.textAnim ?? null,
          fontStyle: ts.fontStyle,
          lineHeight: ts.lineHeight,
          curve: ts.curve,
          blendOpacity: ts.blendOpacity,
          backgroundRoundness: ts.backgroundRoundness,
          backgroundWidth: ts.backgroundWidth,
          backgroundHeight: ts.backgroundHeight,
          backgroundOffsetX: ts.backgroundOffsetX,
          backgroundOffsetY: ts.backgroundOffsetY,
        }, capT);
      });
    }
  }

  /**
   * Paints a single caption around the current origin (0,0) with the SAME styling the
   * worker's `drawCaptionStatic` applies: centered word-wrap at the worker's line-height,
   * an optional rounded background box, a FIXED dark stroke outline + drop shadow (stroke
   * pass), then a crisp fill pass. Geometry/formulas are copied verbatim from the worker so
   * the editor preview shows exactly what the export will bake. A plain caption (no
   * background) still renders identically on both sides — the box block simply no-ops.
   */
  private drawCaptionBody(
    context: CanvasRenderingContext2D,
    text: string,
    fontPx: number,
    weight: number,
    family: string,
    align: CanvasTextAlign,
    color: string,
    background: string | null,
    backgroundOpacity = 1,
    strokeColor: string | null = 'rgba(0, 0, 0, 0.62)',
    strokeWidthRef: number | null = null,
    fx: {
      gradient?: { from: string; to: string; via?: string | null } | null;
      glow?: { color: string; strength?: number; range?: number; offsetX?: number; offsetY?: number } | null;
      shadow?: { color: string; opacity?: number; blur?: number; distance?: number; angle?: number } | null;
      shadow3d?: { color: string; dx?: number; dy?: number } | null;
      bubble?: TextBubbleStyle | null;
      textAnim?: TextAnimSpec | null;
      fontStyle?: 'normal' | 'italic';
      lineHeight?: number;
      curve?: number;
      blendOpacity?: number;
      backgroundRoundness?: number;
      backgroundWidth?: number;
      backgroundHeight?: number;
      backgroundOffsetX?: number;
      backgroundOffsetY?: number;
    } = {},
    captionTime = 0
  ): void {
    const lineHeight = Math.max(fontPx, fontPx * Math.max(0.8, Math.min(2, fx.lineHeight ?? 1.25)));
    const maxWidth = context.canvas.width * 0.84;

    context.save();
    context.globalAlpha *= Math.max(0, Math.min(1, fx.blendOpacity ?? 1));
    context.font = `${fx.fontStyle ?? 'normal'} ${weight} ${fontPx}px ${family}`;
    context.textBaseline = 'middle';
    context.textAlign = align;
    context.lineJoin = 'round';

    const lines = this.wrapCaptionLines(context, text, maxWidth);
    const blockH = lines.length * lineHeight;
    const firstY = 0 - (blockH - lineHeight) / 2;

    // ── Background: a speech-bubble SHAPE, or the flat rounded box (behind all lines) ──
    if (fx.bubble || background) {
      let widest = 0;
      for (const line of lines) widest = Math.max(widest, context.measureText(line).width);
      const bxx = align === 'center' ? -widest / 2 : align === 'right' ? -widest : 0;
      const boxX = bxx - fontPx * 0.4;
      const boxY = firstY - lineHeight / 2 - fontPx * 0.28;
      const boxW = widest + fontPx * 0.8;
      const boxH = blockH + fontPx * 0.56;
      const ref = context.canvas.width / 1080;
      const prevAlpha = context.globalAlpha;
      if (fx.bubble && fx.bubble.color) {
        const box = resolveTextBubbleBox(bxx, firstY - lineHeight / 2, widest, blockH, fontPx, fx.bubble);
        paintTextBubble(context, fx.bubble, box, fontPx, context.canvas.width / 1080);
      } else if (background) {
        const box = resolveTextBackgroundBox(boxX, boxY, boxW, boxH, fontPx, ref, {
          roundness: fx.backgroundRoundness,
          width: fx.backgroundWidth,
          height: fx.backgroundHeight,
          offsetX: fx.backgroundOffsetX,
          offsetY: fx.backgroundOffsetY,
        });
        // Background box alpha (Boxed preset = 0.6); 1 ⇒ opaque, as before.
        const bgAlpha = Math.min(1, Math.max(0, backgroundOpacity));
        context.globalAlpha = prevAlpha * bgAlpha;
        context.fillStyle = background;
        this.roundRect(context, box.x, box.y, box.width, box.height, box.radius);
        context.fill();
      }
      context.globalAlpha = prevAlpha;
    }

    const ref = context.canvas.width / 1080;
    const strokeWidthPx = strokeColor
      ? (strokeWidthRef !== null ? Math.max(0.5, strokeWidthRef * (context.canvas.width / 1080)) : Math.max(fontPx * 0.14, context.canvas.width * 0.006))
      : 0;

    // ── Letter-by-letter animation (typewriter/wave/pop…): draw glyph-by-glyph, then done. ──
    if (fx.textAnim && fx.textAnim.type && !fx.curve) {
      drawAnimatedText(context, lines, 0, firstY, lineHeight, align, fontPx, fx.textAnim, captionTime, {
        color,
        gradient: fx.gradient ?? null,
        strokeColor,
        strokeWidthPx,
        glow: fx.glow ?? null,
        shadow: fx.shadow ?? null,
        referenceScale: ref,
      });
      context.restore();
      return;
    }

    // Neon / outer glow — a blurred halo in the glow color, painted behind the glyphs.
    const glow = resolveTextGlow(fx.glow, fontPx, ref);
    if (glow) {
      context.save();
      context.globalAlpha *= glow.alpha;
      context.shadowColor = glow.color;
      context.shadowBlur = glow.blurPx;
      context.shadowOffsetX = glow.offsetX;
      context.shadowOffsetY = glow.offsetY;
      context.fillStyle = glow.color;
      for (let pass = 0; pass < 3; pass++) {
        lines.forEach((line, i) => drawCurvedTextLine(context, line, 0, firstY + i * lineHeight, fontPx, fx.curve, 'fill'));
      }
      context.restore();
    }

    const shadow = resolveTextShadow(fx.shadow, fontPx, ref);
    if (shadow) {
      context.save();
      context.globalAlpha *= shadow.alpha;
      context.shadowColor = shadow.color;
      context.shadowBlur = shadow.blurPx;
      context.shadowOffsetX = shadow.offsetX;
      context.shadowOffsetY = shadow.offsetY;
      context.fillStyle = shadow.color;
      lines.forEach((line, i) => drawCurvedTextLine(context, line, 0, firstY + i * lineHeight, fontPx, fx.curve, 'fill'));
      context.restore();
    }

    // 3D / retro extrude — offset copies in the shadow color, far→near, behind the face.
    if (fx.shadow3d && fx.shadow3d.color) {
      const dx = typeof fx.shadow3d.dx === 'number' ? fx.shadow3d.dx * ref : 0;
      const dy = typeof fx.shadow3d.dy === 'number' ? fx.shadow3d.dy * ref : fontPx * 0.09;
      const steps = Math.min(28, Math.max(1, Math.round(Math.max(Math.abs(dx), Math.abs(dy)))));
      context.save();
      context.shadowColor = 'transparent';
      context.shadowBlur = 0;
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      context.fillStyle = fx.shadow3d.color;
      for (let s = steps; s >= 1; s--) {
        const fxo = (dx * s) / steps;
        const fyo = (dy * s) / steps;
        lines.forEach((line, i) => drawCurvedTextLine(context, line, fxo, firstY + i * lineHeight + fyo, fontPx, fx.curve, 'fill'));
      }
      context.restore();
    }

    // Outline stroke + drop shadow — color/width honor the authored Text-tool style with
    // the SAME fallbacks the worker uses (undefined ⇒ legacy dark; null ⇒ none). Shadow
    // rides the stroke pass, so preview == export for every preset.
    if (strokeColor) {
      const strokeWidth = strokeWidthRef !== null
        ? Math.max(0.5, strokeWidthRef * (context.canvas.width / 1080))
        : Math.max(fontPx * 0.14, context.canvas.width * 0.006);
      context.lineWidth = strokeWidth;
      context.strokeStyle = strokeColor;
      context.shadowColor = shadow ? 'transparent' : 'rgba(0, 0, 0, 0.45)';
      context.shadowBlur = shadow ? 0 : Math.max(2, fontPx * 0.08);
      context.shadowOffsetY = shadow ? 0 : Math.max(1, fontPx * 0.03);
      lines.forEach((line, i) => drawCurvedTextLine(context, line, 0, firstY + i * lineHeight, fontPx, fx.curve, 'stroke'));
    }

    // Fill pass (no shadow → crisp glyphs). Solid color, or a vertical gradient across the block.
    context.shadowColor = 'transparent';
    context.shadowBlur = 0;
    context.shadowOffsetX = 0;
    context.shadowOffsetY = 0;
    if (fx.gradient && fx.gradient.from && fx.gradient.to) {
      const top = firstY - lineHeight / 2;
      const grad = context.createLinearGradient(0, top, 0, top + blockH);
      grad.addColorStop(0, fx.gradient.from);
      if (fx.gradient.via) grad.addColorStop(0.5, fx.gradient.via);
      grad.addColorStop(1, fx.gradient.to);
      context.fillStyle = grad;
    } else {
      context.fillStyle = color;
    }
    lines.forEach((line, i) => drawCurvedTextLine(context, line, 0, firstY + i * lineHeight, fontPx, fx.curve, 'fill'));

    context.restore();
  }

  /** Greedy word-wrap to a max pixel width — identical algorithm to the worker's `wrapLines`. */
  private wrapCaptionLines(context: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const out: string[] = [];
    for (const paragraph of text.split(/\r?\n/)) {
      const words = paragraph.split(/\s+/).filter(Boolean);
      if (!words.length) { out.push(''); continue; }
      let line = '';
      for (const word of words) {
        const test = line ? `${line} ${word}` : word;
        if (context.measureText(test).width > maxWidth && line) {
          out.push(line);
          line = word;
        } else {
          line = test;
        }
      }
      if (line) out.push(line);
    }
    return out.length ? out : [text];
  }

  /**
   * The caption font-family string, mirroring the worker's `safeFontFamily`. The only
   * non-default family the editor emits is the bundled Urdu face (`language === 'ur'` →
   * family 'urdu'); everything else uses the SAME system stack the worker falls back to,
   * so a normal caption's metrics match the export. A system fallback is always appended.
   */
  private captionFontFamily(style: { fontFamily?: string; language?: string | null } | null | undefined): string {
    const fallback = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
    if (style?.language === 'ur') return `'urdu', ${fallback}`;
    const custom = (style?.fontFamily || '').trim();
    if (custom) {
      // Fire-and-forget: until the face resolves the canvas paints the fallback for a
      // frame or two, then the rAF loop repaints with the real glyphs (dedup inside).
      void ensureEditorFontInDocument(custom);
      return `'${custom}', ${fallback}`;
    }
    return fallback;
  }

  private positiveOr(value: number | null | undefined, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
  }

  /**
   * Draw every active transition clip. A TRUE A↔B blend (crossfade/wipe/slide/push) resolves
   * its outgoing (A) and incoming (B) visual neighbors via the SHARED helper — the SAME one
   * the worker uses — decodes both their frames, and composites them (so preview == export).
   * A stylistic primitive (glitch/flash/iris/blinds/pixel-blocks/zoom-hit/spin-hint, or a
   * dip-to-color the helper doesn't classify) keeps its existing color overlay → no regression.
   */
  private async drawTransitionClips(
    context: CanvasRenderingContext2D,
    project: EditorProject,
    timeSeconds: number,
    options: CanvasPreviewOptions
  ): Promise<void> {
    const transitionClips = this.clipsFromVisibleTracks(project)
      .filter(clip => this.isActive(clip, timeSeconds) && clip.kind === 'transition' && !!clip.transition);
    if (!transitionClips.length) return;

    // ALL visual clips on video/overlay tracks, in the SAME bottom→top order the worker
    // flattens (mirrors render.worker's visualClips), used for neighbor resolution.
    const visualClips = this.allVisualClipsOrdered(project);
    const neighborShapes: TransitionNeighborClip[] = visualClips.map(c => ({
      id: c.id, startSeconds: c.startSeconds, endSeconds: c.endSeconds, kind: c.kind,
    }));
    const clipById = new Map(visualClips.map(c => [c.id, c] as const));

    for (const clip of transitionClips) {
      const params = clip.transition;
      if (!params) continue;
      const duration = Math.max(0.1, clip.endSeconds - clip.startSeconds);
      const elapsed = Math.max(0, timeSeconds - clip.startSeconds);
      const progress = Math.min(1, elapsed / duration);

      // Is this a TRUE A↔B blend? If so, resolve neighbors and composite their frames.
      // glId routes the blend through the WebGL pass inside composeTransitionBlend (so a
      // gl-transition shader previews exactly as it renders); 2D blends ignore it.
      const plan = resolveTransitionBlendPlan({ primitive: params.primitive, angle: params.angle, glId: params.glId }, progress);
      if (plan) {
        const { from, to } = resolveTransitionNeighbors(clip.startSeconds, clip.endSeconds, neighborShapes);
        const fromClip = from ? clipById.get(from.id) ?? null : null;
        const toClip = to ? clipById.get(to.id) ?? null : null;
        if (fromClip || toClip) {
          await this.composeTransitionBlend(context, project, fromClip, toClip, plan, timeSeconds, options);
          continue; // blended — skip the color overlay for this transition.
        }
        // No neighbors found (e.g. a lone clip) → fall through to the overlay below.
      }

      // Stylistic primitive (or no neighbors): keep the existing color-flash overlay.
      const opacity = Math.min(1, Math.max(0, params.opacity ?? 0.9));
      drawTransitionPrimitive(context, params, opacity, context.canvas.width, context.canvas.height, elapsed, progress);
    }
  }

  /**
   * Composite a TRUE A↔B transition blend onto `context`. Built on an off-screen scratch
   * canvas (so the crossfade is EXACTLY A·(1−p)+B·p — see the worker's drawTransitionBlend
   * for the identical reasoning), then blitted once over the already-drawn visuals. Each
   * neighbor frame is fetched via the mediabunny decoder (reusing getOwnedDrawableAt for the
   * same-source case) and drawn cover-fit into the rect the clip occupies, so the blend is
   * geometrically continuous with the surrounding footage.
   */
  private async composeTransitionBlend(
    context: CanvasRenderingContext2D,
    project: EditorProject,
    fromClip: EditorClip | null,
    toClip: EditorClip | null,
    plan: TransitionBlendPlan,
    timeSeconds: number,
    options: CanvasPreviewOptions
  ): Promise<void> {
    const W = context.canvas.width;
    const H = context.canvas.height;
    const scratch = this.getBlendScratch(W, H);
    const sctx = scratch.getContext('2d');
    if (!sctx) return;
    sctx.clearRect(0, 0, W, H);

    // Resolve both neighbor frames first (await), THEN paint — so partial decodes don't
    // leave a half-blended scratch.
    const fromFrame = fromClip ? await this.decodeClipFrame(project, fromClip, timeSeconds, options, false) : null;
    const toFrame = toClip ? await this.decodeClipFrame(project, toClip, timeSeconds, options, true) : null;
    if (!fromFrame && !toFrame) return;

    // Resolve each neighbor's transform at the SAME clamped time the worker uses (its own
    // window-clamped time), so an animated neighbor's transform matches export exactly.
    const fromT = fromClip ? Math.min(Math.max(timeSeconds, fromClip.startSeconds), fromClip.endSeconds - 1e-3) : 0;
    const toT = toClip ? Math.min(Math.max(timeSeconds, toClip.startSeconds), toClip.endSeconds - 1e-3) : 0;

    // WebGL shader transition (preview == export) when the plan carries a glId and BOTH
    // neighbour frames exist. Mirrors the worker's drawTransitionBlendGl; on success the
    // frames are released and we return. Any failure falls through to the 2D blend below
    // (frames stay open for it and are closed at the end) — additive / never-breaks.
    if (
      plan.glId && fromFrame && fromClip && toFrame && toClip &&
      this.composeTransitionBlendGl(context, fromClip, fromFrame, fromT, toClip, toFrame, toT, plan, W, H)
    ) {
      fromFrame.close();
      toFrame.close();
      return;
    }

    switch (plan.kind) {
      case 'crossfade': {
        if (fromFrame && fromClip) this.drawNeighborClipFrame(sctx, fromClip, fromFrame, fromT, crossfadeFromAlpha(plan), 0, 0);
        if (toFrame && toClip) this.drawNeighborClipFrame(sctx, toClip, toFrame, toT, crossfadeToAlpha(plan), 0, 0);
        break;
      }
      case 'wipe': {
        if (fromFrame && fromClip) this.drawNeighborClipFrame(sctx, fromClip, fromFrame, fromT, 1, 0, 0);
        if (toFrame && toClip) {
          const r = wipeRevealRect(plan, W, H);
          sctx.save();
          sctx.beginPath();
          sctx.rect(r.x, r.y, r.w, r.h);
          sctx.clip();
          this.drawNeighborClipFrame(sctx, toClip, toFrame, toT, 1, 0, 0);
          sctx.restore();
        }
        break;
      }
      case 'slide':
      case 'push': {
        if (fromFrame && fromClip) {
          const aOff = plan.kind === 'push' ? slideOutgoingOffset(plan, W, H) : { dx: 0, dy: 0 };
          this.drawNeighborClipFrame(sctx, fromClip, fromFrame, fromT, 1, aOff.dx, aOff.dy);
        }
        if (toFrame && toClip) {
          const bOff = slideIncomingOffset(plan, W, H);
          this.drawNeighborClipFrame(sctx, toClip, toFrame, toT, 1, bOff.dx, bOff.dy);
        }
        break;
      }
    }

    // Release decoded frames (no-op for reader-owned; real close for owned/getSample).
    fromFrame?.close();
    toFrame?.close();

    context.drawImage(scratch, 0, 0);
  }

  /**
   * Composite a TRUE A↔B transition through a gl-transition shader (preview == export).
   * Each neighbour is rasterised onto a COPY of the current composite (so areas the clip
   * doesn't cover keep the surrounding footage, exactly like the worker's
   * drawTransitionBlendGl), the shader runs A→B at the window's RAW progress
   * (gl-transitions ease internally), and the result is blitted over `context`. Returns
   * false on any failure → caller keeps the 2D blend.
   */
  private composeTransitionBlendGl(
    context: CanvasRenderingContext2D,
    fromClip: EditorClip,
    fromFrame: NeighborFrame,
    fromT: number,
    toClip: EditorClip,
    toFrame: NeighborFrame,
    toT: number,
    plan: TransitionBlendPlan,
    W: number,
    H: number
  ): boolean {
    const glId = plan.glId;
    if (!glId) return false;
    const def = (GL_TRANSITIONS as Record<string, GlTransitionRunnerDef>)[glId];
    if (!def) return false;
    const runner = getGlTransitionRunner();
    if (!runner.available()) return false;

    const a = this.getGlScratch('a', W, H);
    const b = this.getGlScratch('b', W, H);
    const actx = a.getContext('2d');
    const bctx = b.getContext('2d');
    if (!actx || !bctx) return false;

    // Each neighbour sits on a COPY of the composite already drawn this frame, so areas
    // the clip doesn't cover stay continuous and the shader samples two opaque frames.
    actx.clearRect(0, 0, W, H);
    actx.drawImage(context.canvas, 0, 0);
    this.drawNeighborClipFrame(actx, fromClip, fromFrame, fromT, 1, 0, 0);

    bctx.clearRect(0, 0, W, H);
    bctx.drawImage(context.canvas, 0, 0);
    this.drawNeighborClipFrame(bctx, toClip, toFrame, toT, 1, 0, 0);

    const out = runner.render({
      from: a as unknown as TexImageSource,
      to: b as unknown as TexImageSource,
      width: W,
      height: H,
      def,
      progress: plan.progressRaw ?? plan.progress,
      uniforms: plan.glUniforms,
    });
    if (!out) return false;
    context.drawImage(out as unknown as CanvasImageSource, 0, 0, W, H);
    return true;
  }

  /** Two reusable scratch canvases for the gl-transition A/B textures (separate from the
   *  single 2D blend scratch so a gl attempt never clobbers the 2D fallback's buffer). */
  private glScratchA: HTMLCanvasElement | null = null;
  private glScratchB: HTMLCanvasElement | null = null;
  private getGlScratch(which: 'a' | 'b', width: number, height: number): HTMLCanvasElement {
    let c = which === 'a' ? this.glScratchA : this.glScratchB;
    if (!c) {
      c = document.createElement('canvas');
      if (which === 'a') this.glScratchA = c; else this.glScratchB = c;
    }
    if (c.width !== width) c.width = width;
    if (c.height !== height) c.height = height;
    return c;
  }

  /**
   * Draw one transition-neighbor clip's frame cover-fit into the rect the clip occupies,
   * at `alpha`, optionally shifted (px) for slide/push. Reuses the SAME center-anchored
   * transform path (withClipTransform → drawCoverDrawable / drawCover) the visual pass uses,
   * so the blended frame lands exactly where the clip draws normally.
   */
  private drawNeighborClipFrame(
    context: CanvasRenderingContext2D,
    clip: EditorClip,
    frame: NeighborFrame,
    resolveTimeSeconds: number,
    alpha: number,
    offsetX: number,
    offsetY: number
  ): void {
    if (alpha <= 0) return;
    context.save();
    context.globalAlpha = this.clampNumber(alpha, 0, 1);
    if (offsetX || offsetY) context.translate(offsetX, offsetY);
    this.withClipTransform(context, clip, context.canvas.width / 2, context.canvas.height / 2, resolveTimeSeconds, () => {
      if (frame.kind === 'image') {
        this.drawCover(context, frame.image, context.canvas.width, context.canvas.height, -context.canvas.width / 2, -context.canvas.height / 2);
      } else {
        this.drawCoverDrawable(context, frame.drawable, context.canvas.width, context.canvas.height, -context.canvas.width / 2, -context.canvas.height / 2);
      }
    });
    context.restore();
  }

  /**
   * Decode ONE transition-neighbor clip's visual frame (video via mediabunny, or an image).
   * The source time is the clip's own mapping, CLAMPED into the clip's window so A (which may
   * end before now) and B (which may start after now) still yield a representative boundary
   * frame. `isIncoming` controls only which mediabunny accessor to prefer for the same-source
   * case: we use getOwnedDrawableAt so a neighbor never disturbs the streaming reader the main
   * visual pass uses for that source (the preview mirror of the worker's getOwnedDrawable).
   * Returns null when the source can't be decoded (the blend then just skips that side).
   */
  private async decodeClipFrame(
    project: EditorProject,
    clip: EditorClip,
    timeSeconds: number,
    options: CanvasPreviewOptions,
    isIncoming: boolean
  ): Promise<NeighborFrame | null> {
    const asset = project.assets.find(item => item.id === clip.assetId);
    if (!asset) return null;
    const url = asset.objectUrl || asset.sourceUrl;
    if (!url) return null;
    const clampedT = Math.min(Math.max(timeSeconds, clip.startSeconds), clip.endSeconds - 1e-3);

    try {
      if (asset.kind === 'image' || clip.kind === 'image') {
        const image = await this.loadImage(url);
        return { kind: 'image', image, close: () => { /* cached image, no per-frame handle */ } };
      }
      const rate = clip.playbackRate && clip.playbackRate > 0 ? clip.playbackRate : 1;
      const sourceTime = Math.max(0, (clampedT - clip.startSeconds) * rate + (clip.sourceStartSeconds || 0));
      const decodeUrl = await this.resolveDecodeUrl(url);
      this.lastDecodeUrlFor.set(url, decodeUrl);
      // Neighbor frames always take an OWNED seek (getOwnedDrawableAt) so they don't advance
      // the shared streaming reader that the main visual pass may be using for this same
      // source on this frame. (isIncoming is accepted for symmetry/future tuning; both sides
      // use the owned path.)
      void isIncoming;
      const mb = await this.mediabunny.getOwnedDrawableAt(decodeUrl, sourceTime);
      if (mb === MEDIABUNNY_FALLBACK || mb === null) return null;
      return { kind: 'video', drawable: mb, close: () => mb.close() };
    } catch {
      return null;
    }
  }

  /** Reusable chroma-key scratch context (persistent; created willReadFrequently). */
  private chromaScratchCtx: CanvasRenderingContext2D | null = null;

  /** Per-size reusable scratch canvas for building transition blends (kept sized to output). */
  private blendScratch: HTMLCanvasElement | null = null;
  private getBlendScratch(width: number, height: number): HTMLCanvasElement {
    if (!this.blendScratch) this.blendScratch = document.createElement('canvas');
    if (this.blendScratch.width !== width) this.blendScratch.width = width;
    if (this.blendScratch.height !== height) this.blendScratch.height = height;
    return this.blendScratch;
  }

  /**
   * ALL visual clips (video/image) across video + overlay tracks, in the SAME bottom→top
   * order the render worker flattens them (render.worker's visualClips) — NOT filtered by
   * active time, because transition neighbor resolution must see clips that end just before
   * or start just after the window. Hidden tracks are excluded (they don't render).
   */
  private allVisualClipsOrdered(project: EditorProject): EditorClip[] {
    const ordered: EditorClip[] = [];
    const videoTracks = project.tracks.filter(track => track.kind === 'video');
    const overlayTracks = project.tracks.filter(track => track.kind === 'overlay');
    for (const track of [...videoTracks, ...overlayTracks]) {
      if (track.hidden) continue;
      for (const clip of track.clips) {
        if (!clip.assetId) continue;
        if (clip.kind !== 'video' && clip.kind !== 'image') continue;
        ordered.push(clip);
      }
    }
    return ordered;
  }

  private drawEffectClips(
    context: CanvasRenderingContext2D,
    project: EditorProject,
    timeSeconds: number,
    vision: HumanVisionResult | null,
  ): void {
    const effectClips = this.clipsFromVisibleTracks(project)
      .filter(clip => this.isActive(clip, timeSeconds) && clip.kind === 'effect' && !!clip.effect);

    for (const clip of effectClips) {
      const params = clip.effect;
      if (!params) continue;
      if (params.primitive === 'filter') continue; // handled once over the visual composite
      const duration = Math.max(0.1, clip.endSeconds - clip.startSeconds);
      const elapsed = Math.max(0, timeSeconds - clip.startSeconds);
      const progress = Math.min(1, elapsed / duration);
      const detectionTarget = humanVisionTargetForEffect({
        params: params as unknown as Record<string, unknown>,
      });
      const linkedProgram = params.glId && isEffectSceneProgram(params.sceneProgram)
        && params.sceneProgram.id === params.glId
        ? params.sceneProgram
        : null;
      if (linkedProgram?.surface === 'body' || isTrustedBodyRuntimeId(params.glId)) {
        // Downloaded body programs retain the exact same detector gate as bundled body
        // effects. Without the declared/satisfied target they are skipped, never painted
        // as a misleading full-frame Canvas primitive.
        if (!detectionTarget || !humanVisionResultSatisfiesTarget(vision, detectionTarget)) continue;
        this.tryGlEffectPreview(context, params, elapsed, progress, vision, 'body');
        continue;
      }
      if (detectionTarget) {
        if (!humanVisionResultSatisfiesTarget(vision, detectionTarget)) continue;
        if (params.glId) this.tryGlEffectPreview(context, params, elapsed, progress, vision, 'body');
        continue;
      }
      // WebGL shader effect / LUT (preview == export) when the effect carries a glId.
      // Any failure (no WebGL2, unknown id, compile error) → keep the 2D primitive.
      const opacity = Math.min(1, Math.max(0, (params.opacity ?? 0.3) * (params.intensity ?? 1)));
      if (params.glId && this.tryGlEffectPreview(context, params, elapsed, progress)) {
        if (params.primitive === 'text-fireworks-ii') {
          drawEffectPrimitive(context, params, opacity, context.canvas.width, context.canvas.height, elapsed, progress);
        }
        continue;
      }
      if (this.tryEffectSceneCpuPreview(context, params, progress)) continue;
      const cpuParams = this.effectCpuFallbackParams(params);
      const cpuOpacity = Math.min(1, Math.max(0, (cpuParams.opacity ?? 0.3) * (cpuParams.intensity ?? 1)));
      drawEffectPrimitive(context, cpuParams, cpuOpacity, context.canvas.width, context.canvas.height, elapsed, progress);
    }
  }

  /** Apply active Filters-track clips to the visual composite before other overlays. */
  private drawTimedFilterClips(
    context: CanvasRenderingContext2D,
    project: EditorProject,
    timeSeconds: number,
  ): void {
    const clips = this.clipsFromVisibleTracks(project)
      .filter(clip =>
        this.isActive(clip, timeSeconds)
        && clip.kind === 'effect'
        && clip.effect?.primitive === 'filter',
      );
    if (!clips.length) return;

    const width = context.canvas.width;
    const height = context.canvas.height;
    const scratch = this.getBlendScratch(width, height);
    const scratchContext = scratch.getContext('2d');
    if (!scratchContext) return;

    for (const clip of clips) {
      const adjust = filterEffectAdjust(clip.effect as unknown as Record<string, unknown>);
      if (!adjust || !adjustIsActive(adjust)) continue;
      scratchContext.setTransform(1, 0, 0, 1, 0, 0);
      scratchContext.clearRect(0, 0, width, height);
      scratchContext.drawImage(context.canvas, 0, 0);

      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, width, height);
      context.filter = adjustFilterCss(adjust, width / 1080);
      context.drawImage(scratch, 0, 0);
      context.filter = 'none';
      if (adjustHasOverlay(adjust)) paintAdjustOverlays(context, adjust, 0, 0, width, height);
      context.restore();
    }
  }

  /**
   * Apply a WebGL shader effect / color-grade LUT to the live preview by reading the
   * current composite as the source texture and blitting the shaded result back — the
   * SAME path the worker compositor (drawEffectShaderGl) and the catalog tiles use, so
   * the editor scrub matches the exported MP4. Returns false on any failure so the
   * caller falls back to the 2D primitive (additive / never-breaks-the-preview).
   */
  private tryGlEffectPreview(
    context: CanvasRenderingContext2D,
    params: NonNullable<EditorClip['effect']>,
    elapsed: number,
    progress: number,
    vision: HumanVisionResult | null = null,
    surface: 'video' | 'body' = 'video',
  ): boolean {
    const glId = params.glId;
    if (!glId) return false;
    const sceneProgram = this.effectSceneProgramFor(params, surface);
    const bundledDef = (GL_EFFECTS as Record<string, GlEffectRunnerDef>)[glId];
    const def: GlEffectRunnerDef | undefined = sceneProgram
      ? {
          id: sceneProgram.id,
          source: sceneProgram.gpu.source,
          uniforms: sceneProgram.gpu.uniforms,
        }
      : bundledDef;
    if (!def) return false;
    const runner = getGlEffectRunner();
    if (!runner.available()) return false;
    const W = context.canvas.width;
    const H = context.canvas.height;
    const intensity = typeof params.intensity === 'number' ? params.intensity : 1;
    const out = runner.render({
      source: context.canvas as unknown as TexImageSource,
      sceneSource: this.readyEffectSceneImage(params.sceneAssetUrl) ?? undefined,
      width: W,
      height: H,
      def,
      intensity: intensity > 0 ? intensity : 1,
      time: elapsed * Math.max(0.05, params.speed ?? 1),
      progress,
      uniforms: sceneProgram
        ? this.effectSceneUniformOverrides(sceneProgram, params.glUniforms)
        : params.glUniforms,
      vision,
    });
    if (!out) return false;
    context.drawImage(out as unknown as CanvasImageSource, 0, 0, W, H);
    return true;
  }

  /**
   * Decode only scene plates whose effect window is active. The explicit Download
   * action has already filled CatalogCache, so the common path is a local blob read;
   * saved projects can still recover by fetching the durable release URL here.
   */
  private async prepareActiveEffectSceneImages(
    project: EditorProject,
    timeSeconds: number,
  ): Promise<void> {
    const urls = new Set(
      this.clipsFromVisibleTracks(project)
        .filter(clip => this.isActive(clip, timeSeconds) && clip.kind === 'effect')
        .map(clip => clip.effect?.sceneAssetUrl?.trim() ?? '')
        .filter(Boolean),
    );
    if (!urls.size) return;
    await Promise.all([...urls].map(url => this.effectSceneImage(url)));
  }

  private effectSceneImage(url: string | null | undefined): Promise<HTMLImageElement | null> {
    const key = url?.trim() ?? '';
    if (!key) return Promise.resolve(null);
    const cached = this.effectSceneImages.get(key);
    if (cached) return cached;
    const load = this.catalogCache.getAsset(key)
      .then(localUrl => this.loadImage(localUrl))
      .then(image => {
        this.resolvedEffectSceneImages.set(key, image);
        return image;
      })
      .catch(() => null);
    this.effectSceneImages.set(key, load);
    return load;
  }

  private readyEffectSceneImage(url: string | null | undefined): HTMLImageElement | null {
    const key = url?.trim() ?? '';
    if (!key) return null;
    // prepareActiveEffectSceneImages awaits the load before drawEffectClips.
    return this.resolvedEffectSceneImages.get(key) ?? null;
  }

  /** Canvas-2D parity path used while WebGL warms or on CPU-only devices. */
  private tryEffectSceneCpuPreview(
    context: CanvasRenderingContext2D,
    params: NonNullable<EditorClip['effect']>,
    progress: number,
  ): boolean {
    const scene = this.readyEffectSceneImage(params.sceneAssetUrl);
    if (!scene) return false;
    const concept = typeof params.sceneConcept === 'number'
      ? params.sceneConcept
      : Number(params.glUniforms?.['sceneConcept'] ?? -1);
    if (!Number.isFinite(concept) || concept < 0 || concept > 9) return false;

    const width = context.canvas.width;
    const height = context.canvas.height;
    const layout = effectSceneCanvasLayout(concept, width, height, progress);
    if (layout.envelope <= 0.00001) return true;
    const source = this.effectSceneScratch('source', width, height);
    const composite = this.effectSceneScratch('composite', width, height);
    const sourceContext = source.getContext('2d');
    const compositeContext = composite.getContext('2d');
    if (!sourceContext || !compositeContext) return false;

    sourceContext.setTransform(1, 0, 0, 1, 0, 0);
    sourceContext.clearRect(0, 0, width, height);
    sourceContext.drawImage(context.canvas, 0, 0);

    compositeContext.setTransform(1, 0, 0, 1, 0, 0);
    compositeContext.clearRect(0, 0, width, height);
    compositeContext.save();
    compositeContext.translate(layout.portalCenterX, layout.portalCenterY);
    compositeContext.scale(layout.portalZoom, layout.portalZoom);
    compositeContext.translate(-layout.portalCenterX, -layout.portalCenterY);
    compositeContext.translate(width / 2, height / 2);
    compositeContext.rotate(layout.plateRotation);
    compositeContext.scale(layout.plateZoom, layout.plateZoom);
    compositeContext.translate(-width / 2, -height / 2);
    compositeContext.drawImage(
      scene,
      layout.plateDrawX,
      layout.plateDrawY,
      layout.plateDrawWidth,
      layout.plateDrawHeight,
    );
    compositeContext.restore();

    compositeContext.save();
    this.traceEffectSceneOpening(compositeContext, layout);
    compositeContext.shadowColor = 'rgba(0,0,0,.72)';
    compositeContext.shadowBlur = Math.max(8, Math.min(width, height) * .035);
    compositeContext.fillStyle = 'rgba(0,0,0,.2)';
    compositeContext.fill();
    compositeContext.clip();
    this.drawContainedSourceIntoSceneQuad(
      compositeContext,
      source,
      layout,
    );
    compositeContext.restore();

    compositeContext.save();
    this.traceEffectSceneOpening(compositeContext, layout);
    compositeContext.lineWidth = Math.max(1.5, Math.min(width, height) * .004);
    compositeContext.strokeStyle = 'rgba(255,255,255,.32)';
    compositeContext.stroke();
    compositeContext.restore();

    context.save();
    context.globalAlpha = Math.min(1, Math.max(0, params.intensity ?? 1)) * layout.envelope;
    context.drawImage(composite, 0, 0);
    context.restore();
    return true;
  }

  private effectSceneScratch(
    kind: 'source' | 'composite',
    width: number,
    height: number,
  ): HTMLCanvasElement {
    let canvas = kind === 'source'
      ? this.effectSceneSourceScratch
      : this.effectSceneCompositeScratch;
    if (!canvas) {
      canvas = document.createElement('canvas');
      if (kind === 'source') this.effectSceneSourceScratch = canvas;
      else this.effectSceneCompositeScratch = canvas;
    }
    if (canvas.width !== width) canvas.width = width;
    if (canvas.height !== height) canvas.height = height;
    return canvas;
  }

  private traceEffectSceneOpening(
    context: CanvasRenderingContext2D,
    layout: EffectSceneCanvasLayout,
  ): void {
    const [topLeft, topRight, bottomRight, bottomLeft] = layout.corners;
    context.beginPath();
    context.moveTo(topLeft[0], topLeft[1]);
    context.lineTo(topRight[0], topRight[1]);
    context.lineTo(bottomRight[0], bottomRight[1]);
    context.lineTo(bottomLeft[0], bottomLeft[1]);
    context.closePath();
  }

  /**
   * Perspective-aware object-fit: contain. The complete user canvas is mapped
   * into the photographed quadrilateral; it is never center-cropped.
   */
  private drawContainedSourceIntoSceneQuad(
    context: CanvasRenderingContext2D,
    source: HTMLCanvasElement,
    layout: EffectSceneCanvasLayout,
  ): void {
    const [topLeft, topRight, bottomRight, bottomLeft] = layout.corners;
    const topWidth = Math.hypot(topRight[0] - topLeft[0], topRight[1] - topLeft[1]);
    const bottomWidth = Math.hypot(bottomRight[0] - bottomLeft[0], bottomRight[1] - bottomLeft[1]);
    const leftHeight = Math.hypot(bottomLeft[0] - topLeft[0], bottomLeft[1] - topLeft[1]);
    const rightHeight = Math.hypot(bottomRight[0] - topRight[0], bottomRight[1] - topRight[1]);
    const targetAspect = ((topWidth + bottomWidth) * .5)
      / Math.max(1, (leftHeight + rightHeight) * .5);
    const sourceAspect = source.width / Math.max(1, source.height);
    let localLeft = 0;
    let localRight = 1;
    let localTop = 0;
    let localBottom = 1;
    if (targetAspect > sourceAspect) {
      const widthFraction = sourceAspect / targetAspect;
      localLeft = (1 - widthFraction) / 2;
      localRight = 1 - localLeft;
    } else {
      const heightFraction = targetAspect / sourceAspect;
      localTop = (1 - heightFraction) / 2;
      localBottom = 1 - localTop;
    }

    const warpDelta = Math.hypot(
      (bottomRight[0] - bottomLeft[0]) - (topRight[0] - topLeft[0]),
      (bottomRight[1] - bottomLeft[1]) - (topRight[1] - topLeft[1]),
    );
    const stripCount = warpDelta > 1 ? 36 : 1;
    const pointAt = (u: number, v: number): readonly [number, number] => {
      const topX = topLeft[0] + (topRight[0] - topLeft[0]) * u;
      const topY = topLeft[1] + (topRight[1] - topLeft[1]) * u;
      const bottomX = bottomLeft[0] + (bottomRight[0] - bottomLeft[0]) * u;
      const bottomY = bottomLeft[1] + (bottomRight[1] - bottomLeft[1]) * u;
      return [
        topX + (bottomX - topX) * v,
        topY + (bottomY - topY) * v,
      ];
    };

    for (let index = 0; index < stripCount; index += 1) {
      const sourceX0 = source.width * index / stripCount;
      const sourceX1 = source.width * (index + 1) / stripCount;
      const localU0 = localLeft + (localRight - localLeft) * index / stripCount;
      const localU1 = localLeft + (localRight - localLeft) * (index + 1) / stripCount;
      const stripTopLeft = pointAt(localU0, localTop);
      const stripTopRight = pointAt(localU1, localTop);
      const stripBottomRight = pointAt(localU1, localBottom);
      const stripBottomLeft = pointAt(localU0, localBottom);
      const sourceSliceWidth = Math.max(.0001, sourceX1 - sourceX0);
      const a = (stripTopRight[0] - stripTopLeft[0]) / sourceSliceWidth;
      const b = (stripTopRight[1] - stripTopLeft[1]) / sourceSliceWidth;
      const c = (stripBottomLeft[0] - stripTopLeft[0]) / Math.max(1, source.height);
      const d = (stripBottomLeft[1] - stripTopLeft[1]) / Math.max(1, source.height);
      const e = stripTopLeft[0] - a * sourceX0;
      const f = stripTopLeft[1] - b * sourceX0;

      context.save();
      context.beginPath();
      context.moveTo(stripTopLeft[0], stripTopLeft[1]);
      context.lineTo(stripTopRight[0], stripTopRight[1]);
      context.lineTo(stripBottomRight[0], stripBottomRight[1]);
      context.lineTo(stripBottomLeft[0], stripBottomLeft[1]);
      context.closePath();
      context.clip();
      context.setTransform(a, b, c, d, e, f);
      context.drawImage(source, 0, 0);
      context.restore();
    }
  }

  private effectSceneProgramFor(
    params: NonNullable<EditorClip['effect']>,
    surface: 'video' | 'body',
  ): EffectSceneProgram | null {
    const program = params.sceneProgram;
    if (
      !params.glId
      || !isEffectSceneProgram(program)
      || program.id !== params.glId
      || program.surface !== surface
      || (surface === 'video' && isTrustedBodyRuntimeId(params.glId))
    ) return null;
    return program;
  }

  private effectSceneUniformOverrides(
    program: EffectSceneProgram,
    raw: Record<string, number | number[]> | undefined,
  ): Record<string, number | number[]> {
    if (!raw) return {};
    const output: Record<string, number | number[]> = {};
    for (const uniform of program.gpu.uniforms) {
      const candidate = raw[uniform.name];
      const values = typeof candidate === 'number'
        ? [candidate]
        : Array.isArray(candidate) ? candidate : null;
      if (
        !values
        || values.length !== uniform.default.length
        || values.some(value => typeof value !== 'number' || !Number.isFinite(value))
      ) continue;
      output[uniform.name] = values.length === 1 ? values[0] : [...values];
    }
    return output;
  }

  /** Canvas fallback is declarative and limited to the existing safe primitive surface. */
  private effectCpuFallbackParams(
    params: NonNullable<EditorClip['effect']>,
  ): NonNullable<EditorClip['effect']> {
    const program = this.effectSceneProgramFor(params, 'video');
    if (!program || !isTrustedEffectPrimitive(program.cpu.primitive)) return params;
    const output = { ...params, primitive: program.cpu.primitive };
    const allowed = new Set([
      'color', 'color2', 'opacity', 'speed', 'angle', 'size', 'blend', 'intensity',
    ]);
    for (const [key, value] of Object.entries(program.cpu.params ?? {})) {
      if (!allowed.has(key)) continue;
      if (
        value === null
        || typeof value === 'boolean'
        || typeof value === 'string'
        || (typeof value === 'number' && Number.isFinite(value))
        || (
          Array.isArray(value)
          && value.length <= 4
          && value.every(component => typeof component === 'number' && Number.isFinite(component))
        )
      ) {
        (output as unknown as Record<string, unknown>)[key] = Array.isArray(value)
          ? [...value]
          : value;
      }
    }
    return output;
  }

  private drawStickerClips(context: CanvasRenderingContext2D, project: EditorProject, timeSeconds: number): void {
    const stickerClips = this.clipsFromVisibleTracks(project)
      .filter(clip => this.isActive(clip, timeSeconds) && clip.kind === 'sticker' && !!clip.sticker);

    for (const clip of stickerClips) {
      const params = clip.sticker;
      if (!params) continue;
      this.withClipTransform(context, clip, context.canvas.width / 2, context.canvas.height / 2, timeSeconds, () => {
        drawStickerPrimitive(context, params, context.canvas.width);
      });
    }
  }

  /**
   * The set of source-video clip ids that should be audible this frame. Prefers the
   * multi-clip `audibleSourceClipIds` (A4); falls back to wrapping the legacy single
   * `audibleSourceClipId` so existing callers/tests keep their one-clip behavior.
   */
  private audibleSet(options: CanvasPreviewOptions): ReadonlySet<string> | null {
    if (options.audibleSourceClipIds) return options.audibleSourceClipIds;
    if (options.audibleSourceClipId) return new Set([options.audibleSourceClipId]);
    return null;
  }

  private async drawAsset(
    context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    projectId: string,
    asset: EditorAsset,
    clip: EditorClip,
    timeSeconds: number,
    options: CanvasPreviewOptions,
    activeVideoElements?: Set<HTMLVideoElement>,
    seenSourceDecodeUrls?: Set<string>,
    isMasterClock = true,
    motion?: CombinedMotionDelta,
    allowOutsideCanvas = false,
  ): Promise<PreviewMediaClock | null> {
    const url = asset.objectUrl || asset.sourceUrl;
    if (!url) return null;
    try {
      if (asset.kind === 'image' || clip.kind === 'image') {
        const image = await this.loadImage(url);
        this.withClipTransform(context, clip, canvas.width / 2, canvas.height / 2, timeSeconds, () => {
          this.drawMediaFrame(context, clip, canvas.width, canvas.height, timeSeconds, (ctx) =>
            this.drawCover(ctx, image, canvas.width, canvas.height, -canvas.width / 2, -canvas.height / 2, clip.fitMode));
        }, motion, allowOutsideCanvas);
        return null;
      }
      if (asset.kind === 'video' || clip.kind === 'video') {
        const allowTaintedPreview = Boolean(options.allowTaintedPreview);
        const rate = clip.playbackRate && clip.playbackRate > 0 ? clip.playbackRate : 1;
        // Map output time → source time EXACTLY like the render worker.
        const sourceTime = speedSourceTime(timeSeconds, clip.startSeconds, clip.endSeconds, clip.sourceStartSeconds || 0, clip.speedCurve ?? null, rate);
        const audioVolume = resolvePreviewSourceAudioVolumeMulti(clip, this.audibleSet(options));
        // Speed CURVE: audio plays at the INSTANTANEOUS curve rate (drift is corrected by the
        // existing re-seek), and a curved clip never drives the master clock (nonlinear map).
        const hasSpeedCurve = !!(clip.speedCurve && clip.speedCurve.points && clip.speedCurve.points.length);
        const liveRate = hasSpeedCurve
          ? speedRateAt(clip.speedCurve, (timeSeconds - clip.startSeconds) / Math.max(0.0001, clip.endSeconds - clip.startSeconds), rate)
          : rate;

        // --- Frame source swap: try the frame-accurate mediabunny decoder first. ---
        // It STREAMS frames (no per-frame <video> seek), which is the smooth-scrub win.
        // Image/sticker/text/effect drawing is untouched; only the VIDEO frame source changes.
        // The decoder fetches the URL itself (blob:/data:/http all supported by mediabunny's
        // UrlSource), so we feed it the same resolved-for-fetch URL the <video> would use.
        const decodeUrl = await this.resolveDecodeUrl(url);
        this.lastDecodeUrlFor.set(url, decodeUrl);
        // First active clip of this source URL this frame STREAMS via the shared reader;
        // each additional same-URL clip (duplicate / same-source PiP) OWNS its own seeked
        // frame so it can't advance the shared reader and corrupt the first clip's draw
        // (preview-side mirror of the A1 render bug). Images share one bitmap → no conflict.
        const firstOfSource = !seenSourceDecodeUrls || !seenSourceDecodeUrls.has(decodeUrl);
        seenSourceDecodeUrls?.add(decodeUrl);
        const mbFrame = firstOfSource
          ? await this.mediabunny.getDrawableAt(decodeUrl, sourceTime)
          : await this.mediabunny.getOwnedDrawableAt(decodeUrl, sourceTime);
        const mediabunnyOwnsVisual = mbFrame !== MEDIABUNNY_FALLBACK && mbFrame !== null;

        if (mediabunnyOwnsVisual) {
          // Draw the decoded frame through the SAME transform path images/video used.
          this.withClipTransform(context, clip, canvas.width / 2, canvas.height / 2, timeSeconds, () => {
            this.drawMediaFrame(context, clip, canvas.width, canvas.height, timeSeconds, (ctx) =>
              this.drawCoverDrawable(ctx, mbFrame, canvas.width, canvas.height, -canvas.width / 2, -canvas.height / 2, clip.fitMode));
          }, motion, allowOutsideCanvas);
          // Release the frame (no-op for reader-owned frames; real close for owned/getSample
          // frames inside the decoder — frame-lifecycle discipline mirrors the worker).
          mbFrame.close();

          if (options.isPlaying && !this.previewScrubbing) {
            // The off-DOM <video> is the AUDIO source (+ master clock for the topmost clip).
            // It plays freely (only re-seeking on large drift), so the timeline clock tracks
            // its currentTime — the visual no longer depends on seeking it. For an ADDITIONAL
            // clip that shares this source URL we skip re-driving the one shared element (the
            // first clip already plays that source's audio + owns its seek), so we never
            // re-seek/re-rate/re-volume it twice per frame (would de-pace it).
            if (firstOfSource) {
              const audioVideo = await this.loadVideo(url, allowTaintedPreview, true);
              // A pointer-down can begin while loadVideo() is still resolving. Re-check after
              // that await so the stale draw cannot restart native media during an active scrub.
              if (this.previewScrubbing) {
                audioVideo.pause();
                audioVideo.muted = true;
                return null;
              }
              activeVideoElements?.add(audioVideo);
              this.playVideoPreview(audioVideo, sourceTime, liveRate * (options.playbackRate ?? 1), audioVolume, clip.audioCleanup);
              if (isMasterClock && !hasSpeedCurve) return this.buildPreviewMediaClock(projectId, clip, audioVideo, rate);
            }
          }
          // Paused (or a non-master / additional same-source clip): no clock from here.
          return null;
        }

        // --- Fallback path (unchanged): this source can't be mediabunny-decoded, so
        //     draw it via the off-DOM <video> + currentTime seek as before. ---
        const cacheKey = this.videoCacheKey(url, allowTaintedPreview);
        if (!this.videoReadyKeys.has(cacheKey)) {
          this.drawAssetLoading(context, canvas, asset);
          const showedPoster = await this.drawVideoPoster(context, canvas, asset, clip, timeSeconds);
          if (!showedPoster) this.drawAssetLoading(context, canvas, asset);
        }
        const video = await this.loadVideo(url, allowTaintedPreview, Boolean(options.isPlaying));
        activeVideoElements?.add(video);
        if (options.isPlaying && !this.previewScrubbing) {
          // Same shared-element discipline: only the first same-URL clip drives the element.
          if (firstOfSource) {
            this.playVideoPreview(video, sourceTime, liveRate * (options.playbackRate ?? 1), audioVolume, clip.audioCleanup);
          }
        } else if (!options.isPlaying && firstOfSource) {
          video.muted = true;
          video.pause();
          this.videoPlaybackState.set(video, { targetTime: sourceTime, playing: false });
          await this.seekVideo(video, sourceTime);
        }
        this.withClipTransform(context, clip, canvas.width / 2, canvas.height / 2, timeSeconds, () => {
          this.drawMediaFrame(context, clip, canvas.width, canvas.height, timeSeconds, (ctx) =>
            this.drawCover(ctx, video, canvas.width, canvas.height, -canvas.width / 2, -canvas.height / 2, clip.fitMode));
        }, motion, allowOutsideCanvas);
        return isMasterClock && !hasSpeedCurve ? this.buildPreviewMediaClock(projectId, clip, video, rate) : null;
      }
    } catch {
      this.drawAssetFallback(context, canvas, asset);
    }
    return null;
  }

  getPreviewMediaClock(projectId: string | null | undefined, maxAgeMs = 750): PreviewMediaClock | null {
    const clock = this.previewMediaClock;
    if (!clock) return null;
    if (projectId && clock.projectId !== projectId) return null;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return now - clock.generatedAtMs <= maxAgeMs ? clock : null;
  }

  /**
   * Return a media clock only when it belongs to the clip that owns the requested timeline
   * position. Async preview draws can finish out of order at a cut; project/age validation
   * alone cannot distinguish a fresh clock from the outgoing clip from the incoming clip's
   * clock. Keeping this check beside the canonical master-clip resolver prevents stale clocks
   * from pulling the playhead back across a cut while preserving ordinary same-clip playback.
   */
  getPreviewMediaClockAt(
    project: EditorProject | null | undefined,
    timeSeconds: number,
    maxAgeMs = 750
  ): PreviewMediaClock | null {
    if (!project || !Number.isFinite(timeSeconds)) return null;
    const expectedClipId = this.topmostActiveVideoClipId(
      project,
      this.activeVisualClipsOrdered(project, timeSeconds)
    );
    if (!expectedClipId) return null;
    const clock = this.getPreviewMediaClock(project.id, maxAgeMs);
    return clock?.clipId === expectedClipId ? clock : null;
  }

  /**
   * Whether the latest fresh clock still belongs to the outgoing clip while the requested
   * timeline position already belongs to an adjacent incoming clip. The playback clock uses
   * this precise ownership mismatch to bridge the cut with rAF; a missing clock on initial load
   * or the same clip remains ordinary buffering and keeps the normal wait grace.
   */
  isPreviewMediaClockHandoff(
    project: EditorProject | null | undefined,
    timeSeconds: number,
    maxAgeMs = 750
  ): boolean {
    if (!project || !Number.isFinite(timeSeconds)) return false;
    const expectedClipId = this.topmostActiveVideoClipId(
      project,
      this.activeVisualClipsOrdered(project, timeSeconds)
    );
    if (!expectedClipId) return false;
    const clock = this.getPreviewMediaClock(project.id, maxAgeMs);
    return Boolean(clock && clock.clipId !== expectedClipId);
  }

  /**
   * Bias the cached preview media clock to a new timeline time. Used when the user scrubs/clicks
   * the timeline WHILE playing: without this the playback clock (which reads this cached value)
   * would immediately snap the playhead back to the old position. After this nudge the draw loop
   * seeks the <video> to match and playback simply continues from the new spot — no pause needed.
   */
  nudgePreviewClock(timeSeconds: number): void {
    if (!Number.isFinite(timeSeconds)) return;
    // Record the seek target so buildPreviewMediaClock HOLDS the clock here until the element
    // actually reaches it — otherwise the clock follows the <video>'s unstable mid-seek position
    // and fights the seek (playhead and video oscillating between old and new).
    this.pendingSeekTimelineTime = Math.max(0, timeSeconds);
    const clock = this.previewMediaClock;
    if (clock) {
      this.previewMediaClock = {
        ...clock,
        timelineTimeSeconds: Math.max(0, timeSeconds),
        generatedAtMs: typeof performance !== 'undefined' ? performance.now() : Date.now()
      };
    }
  }

  /**
   * Pause native playback media while the timeline ruler is being dragged. The canvas continues
   * decoding the newest requested frame, while the hidden audio/master video is resynchronised
   * only after release instead of issuing a blob range read for every pointer movement.
   */
  setPreviewScrubbing(active: boolean): void {
    if (this.previewScrubbing === active) return;
    this.previewScrubbing = active;
    if (active) this.pausePreviewVideos();
  }

  resumePreviewVideos(defaultVolume = 1): void {
    for (const video of this.previewVideos) {
      if (video.readyState < HTMLMediaElement.HAVE_METADATA) {
        video.setAttribute('data-jivu-preview-play-request', 'pending-user-resume');
        continue;
      }
      const desiredVolume = Math.max(0, Math.min(2, this.videoDesiredVolume.get(video) ?? defaultVolume));
      video.volume = desiredVolume > 0 ? 1 : 0;
      video.muted = desiredVolume <= 0;
      if (video.paused) {
        video.setAttribute('data-jivu-preview-play-request', 'user-resume');
        void video.play()
          .then(() => video.setAttribute('data-jivu-preview-play-result', 'user-resume-ok'))
          .catch(error => video.setAttribute('data-jivu-preview-play-result', `user-resume-error:${error?.name || 'error'}`));
      }
    }
  }

  pausePreviewVideos(): void {
    for (const video of this.previewVideos) {
      if (!video.paused) video.pause();
      video.muted = true;
    }
  }

  private async drawVideoPoster(
    context: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    asset: EditorAsset,
    clip: EditorClip,
    timeSeconds: number
  ): Promise<boolean> {
    const posterUrl = this.videoPosterUrl(asset);
    if (!posterUrl) return false;
    try {
      const image = await this.loadImage(posterUrl);
      this.withClipTransform(context, clip, canvas.width / 2, canvas.height / 2, timeSeconds, () => {
        this.drawCover(context, image, canvas.width, canvas.height, -canvas.width / 2, -canvas.height / 2);
      });
      return true;
    } catch {
      return false;
    }
  }

  private drawAssetLoading(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement, asset: EditorAsset): void {
    context.save();
    context.fillStyle = 'rgba(15, 23, 42, 0.28)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'rgba(15, 23, 42, 0.72)';
    const boxWidth = Math.min(canvas.width * 0.72, Math.max(240, canvas.width * 0.42));
    const boxHeight = Math.max(86, canvas.height * 0.14);
    const x = (canvas.width - boxWidth) / 2;
    const y = (canvas.height - boxHeight) / 2;
    const radius = Math.max(12, Math.round(canvas.width / 120));
    this.roundRect(context, x, y, boxWidth, boxHeight, radius);
    context.fill();
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = `700 ${Math.max(14, Math.round(canvas.width / 54))}px system-ui`;
    context.fillStyle = 'rgba(255, 255, 255, 0.92)';
    context.fillText('Loading source media...', canvas.width / 2, y + boxHeight * 0.42);
    const label = asset.name?.trim();
    if (label) {
      context.font = `500 ${Math.max(11, Math.round(canvas.width / 78))}px system-ui`;
      context.fillStyle = 'rgba(255, 255, 255, 0.68)';
      context.fillText(label.slice(0, 64), canvas.width / 2, y + boxHeight * 0.68);
    }
    context.restore();
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    const cached = this.imageCache.get(url);
    if (cached) {
      this.imageCache.delete(url);
      this.imageCache.set(url, cached);
      return cached;
    }
    const promise = (async () => {
      const src = await this.resolveSrc(url, 'image');
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        if (!src.startsWith('blob:') && !src.startsWith('data:')) image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Image preview failed'));
        image.src = src;
      });
    })();
    this.imageCache.set(url, promise);
    this.trimImageCache();
    return promise;
  }

  private loadVideo(url: string, allowTaintedPreview = false, autoplayMuted = false): Promise<HTMLVideoElement> {
    const cacheKey = this.videoCacheKey(url, allowTaintedPreview);
    const cached = this.videoCache.get(cacheKey);
    if (cached) {
      // LRU touch only. Do NOT re-trigger muted autoplay here: loadVideo runs on EVERY draw
      // frame (drawAsset calls it per frame), and re-muting + re-playing the element every
      // frame thrashes the media pipeline and breaks its real-time pacing — the source of the
      // ~3x-too-fast preview. From here on playVideoPreview owns play/seek/volume, state-driven.
      this.videoCache.delete(cacheKey);
      this.videoCache.set(cacheKey, cached);
      return cached;
    }
    const promise = (async () => {
      const directSrc = this.directVideoCandidate(url);
      if (directSrc) {
        try {
          return await this.createVideoElement(directSrc, !allowTaintedPreview, autoplayMuted);
        } catch {
          // Fall back to the authenticated proxy when direct R2/Drive playback is blocked by CORS.
        }
      }
      const src = await this.resolveSrc(url, 'video');
      return await this.createVideoElement(src, true, autoplayMuted);
    })().then(video => {
      this.videoReadyKeys.add(cacheKey);
      return video;
    }).catch(error => {
      this.videoReadyKeys.delete(cacheKey);
      this.videoCache.delete(cacheKey);
      throw error;
    });
    this.videoCache.set(cacheKey, promise);
    this.trimVideoCache();
    return promise;
  }

  private createVideoElement(src: string, useCrossOrigin: boolean, autoplayMuted = false): Promise<HTMLVideoElement> {
    return new Promise<HTMLVideoElement>((resolve, reject) => {
      const video = document.createElement('video');
      if (useCrossOrigin && !src.startsWith('blob:') && !src.startsWith('data:')) video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('data-jivu-editor-preview-video', 'true');
      this.previewVideos.add(video);
      video.addEventListener('play', () => video.setAttribute('data-jivu-preview-event', 'play'));
      video.addEventListener('playing', () => video.setAttribute('data-jivu-preview-event', 'playing'));
      video.addEventListener('pause', () => video.setAttribute('data-jivu-preview-event', 'pause'));
      video.addEventListener('waiting', () => video.setAttribute('data-jivu-preview-event', 'waiting'));
      this.attachVideoElement(video);
      let settled = false;
      const timeout = window.setTimeout(() => {
        finishError(new Error('Video preview timed out'));
      }, 30000);
      const cleanup = () => {
        window.clearTimeout(timeout);
        video.removeEventListener('loadedmetadata', onReady);
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('canplay', onReady);
        video.removeEventListener('error', onError);
      };
      const finishSuccess = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(video);
      };
      const finishError = (error: Error) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(error);
      };
      const onReady = () => {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          finishSuccess();
        }
      };
      const onError = () => {
        const detail = video.error?.message || (video.error?.code ? `media error code ${video.error.code}` : 'Video preview failed');
        finishError(new Error(detail));
      };
      video.addEventListener('loadedmetadata', onReady);
      video.addEventListener('loadeddata', onReady);
      video.addEventListener('canplay', onReady);
      video.addEventListener('error', onError);
      video.src = src;
      video.load();
      if (autoplayMuted) {
        this.ensureMutedAutoplay(video);
      }
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        queueMicrotask(onReady);
      }
    });
  }

  private ensureMutedAutoplay(video: HTMLVideoElement): void {
    video.muted = true;
    video.volume = 0;
    if (video.paused) {
      video.setAttribute('data-jivu-preview-play-request', 'autoplay');
      void video.play()
        .then(() => video.setAttribute('data-jivu-preview-play-result', 'autoplay-ok'))
        .catch(error => video.setAttribute('data-jivu-preview-play-result', `autoplay-error:${error?.name || 'error'}`));
    }
  }

  private directVideoCandidate(rawUrl: string): string | null {
    const lower = rawUrl.toLowerCase();
    if (lower.startsWith('blob:') || lower.startsWith('data:')) return rawUrl;
    if (!/^https?:\/\//i.test(rawUrl)) return null;
    // Review/Processing media should use the same fully cached proxy blob as audio.
    // Mixing streamed R2 video frames with blob-backed audio can drift or freeze the
    // canvas preview even when the source MP4 itself is valid.
    return storageKeyFromRenderableUrl(rawUrl) ? null : rawUrl;
  }

  private videoCacheKey(url: string, allowTaintedPreview: boolean): string {
    const lower = url.toLowerCase();
    if (lower.startsWith('blob:') || lower.startsWith('data:')) return `inline:${url}`;
    const storageKey = storageKeyFromRenderableUrl(url);
    if (storageKey) return `storage:${storageKey}`;
    return `${allowTaintedPreview ? 'preview' : 'safe'}:${url}`;
  }

  private videoPosterUrl(asset: EditorAsset): string | null {
    const metadata = asset.metadata ?? {};
    const direct = metadata['thumbnailUrl'] ?? metadata['posterUrl'] ?? metadata['previewUrl'];
    return typeof direct === 'string' && direct.trim() ? direct.trim() : null;
  }

  /**
   * Resolve a clip's media URL to something a bare <video>/<img> element can load.
   * R2 object URLs are authed-proxy-only — an element can't attach the Bearer token, so a
   * direct presigned URL CORS-fails → black preview (while the timeline filmstrip works
   * because it fetches via HttpClient). We mirror the filmstrip: pull the `users/...` storage
   * key and fetch the bytes through the authed pipeline-assets proxy (HttpClient → blob →
   * object URL), cached per key. blob:/data:/external URLs pass through untouched.
   */
  private resolveSrc(rawUrl: string, kind: 'video' | 'image' | 'audio'): Promise<string> {
    const lower = rawUrl.toLowerCase();
    if (lower.startsWith('blob:') || lower.startsWith('data:')) return Promise.resolve(rawUrl);
    const storageKey = storageKeyFromRenderableUrl(rawUrl);
    if (!storageKey) return Promise.resolve(rawUrl);
    const cached = this.blobUrlCache.get(storageKey);
    if (cached) return cached;
    const proxyUrl = buildPipelineAssetProxyUrl(environment.apiUrl, storageKey, guessMediaContentType(storageKey, kind));
    const promise = this.mediaCache.warmRemote(storageKey, proxyUrl)
      .then(entry => entry.objectUrl || rawUrl)
      .catch(() => rawUrl);
    this.blobUrlCache.set(storageKey, promise);
    return promise;
  }

  /**
   * URL the mediabunny decoder should fetch for a video source. R2/Drive presigned
   * URLs CORS-fail for a bare fetch (no Bearer header) exactly as they do for a bare
   * <video>, so we route them through the SAME authed-proxy blob the <video> uses
   * (cached per storage key → stable blob: URL → stable decoder cache key). blob:/
   * data:/external URLs pass straight through. Never rejects — on failure we hand back
   * the raw URL and the decoder may still fail → the caller's <video> fallback covers it.
   */
  private resolveDecodeUrl(rawUrl: string): Promise<string> {
    return this.resolveSrc(rawUrl, 'video').catch(() => rawUrl);
  }

  /**
   * RENDER-PREP hook: resolve a media URL to the SAME authed-proxy `blob:` URL the preview
   * decodes from (cached per storage key). The background render worker fetches its sources
   * itself, and a raw R2/Drive presigned URL CORS-fails inside the worker exactly as it does
   * for a bare <video> — leaving the render stuck on "Loading media…" while the preview plays
   * happily from its proxy blob. Routing the job's sources through this resolver gives the
   * export the same media access as the preview. Never rejects — unresolvable/external URLs
   * pass through unchanged.
   */
  resolveRenderSourceUrl(rawUrl: string, kind: 'video' | 'image' | 'audio' = 'video'): Promise<string> {
    return this.resolveSrc(rawUrl, kind).catch(() => rawUrl);
  }

  /**
   * Strict worker handoff variant. Preview tools may fall back to their native element
   * when a proxy fetch fails, but a background worker has no authenticated fallback.
   * Resolve storage-backed media to the session cache or reject before Mediabunny starts,
   * avoiding a second anonymous worker fetch and its opaque "Failed to fetch" loop.
   */
  async resolveWorkerSourceUrl(
    rawUrl: string,
    kind: 'video' | 'image' | 'audio' = 'video',
  ): Promise<string> {
    const lower = rawUrl.toLowerCase();
    if (lower.startsWith('blob:') || lower.startsWith('data:')) return rawUrl;
    const storageKey = storageKeyFromRenderableUrl(rawUrl);
    if (!storageKey) return rawUrl;

    const existing = this.mediaCache.get(storageKey);
    if (existing?.objectUrl) return existing.objectUrl;

    const proxyUrl = buildPipelineAssetProxyUrl(
      environment.apiUrl,
      storageKey,
      guessMediaContentType(storageKey, kind),
    );
    const entry = await this.mediaCache.warmRemote(storageKey, proxyUrl);
    if (!entry.objectUrl) {
      throw new Error(`Media cache did not create a readable source for "${storageKey}".`);
    }
    this.blobUrlCache.set(storageKey, Promise.resolve(entry.objectUrl));
    return entry.objectUrl;
  }

  /**
   * Real source duration for a clip URL — used to replace the bootstrap's 30s default so the
   * timeline matches the actual video length. Warms the same video cache the preview draws
   * from, so it costs no extra download.
   */
  async probeMediaDuration(rawUrl: string | null | undefined): Promise<number | null> {
    if (!rawUrl) return null;
    try {
      const video = await this.loadVideo(rawUrl);
      const duration = video.duration;
      return Number.isFinite(duration) && duration > 0 ? duration : null;
    } catch {
      return null;
    }
  }

  /**
   * True when this source URL is already fully loaded and ready to play (its proxy blob is
   * cached and the <video> element reached a playable state). Lets the editor skip the
   * "preparing" loader for media that is already warm.
   */
  isSourceReady(url: string | null | undefined): boolean {
    if (!url) return false;
    // Ready when the mediabunny decoder has its video track (the new visual source),
    // OR the <video> proxy blob reached a playable state (fallback / audio path).
    const decodeUrl = this.lastDecodeUrlFor.get(url);
    if (decodeUrl && this.mediabunny.isReady(decodeUrl)) return true;
    return this.videoReadyKeys.has(this.videoCacheKey(url, true))
      || this.videoReadyKeys.has(this.videoCacheKey(url, false));
  }

  /**
   * Pre-load a source video so the editor can gate preview/play behind a loader until the
   * media is fully fetched. Warms BOTH the mediabunny decoder (the new visual source — so
   * the first preview frame is instant and frame-accurate) and the <video> element (audio +
   * the per-source fallback). Resolves once both are warm. Never rejects — on failure the gate
   * simply lifts and the normal lazy path applies.
   */
  async prepareSource(url: string | null | undefined): Promise<void> {
    if (!url) return;
    try {
      const decodeUrl = await this.resolveDecodeUrl(url);
      this.lastDecodeUrlFor.set(url, decodeUrl);
      await Promise.all([
        this.mediabunny.prepare(decodeUrl),
        this.loadVideo(url, true, false),
      ]);
    } catch {
      // ignore — lift the gate; preview keeps its own fallback affordances
    }
  }

  private seekVideo(video: HTMLVideoElement, timeSeconds: number): Promise<void> {
    const previous = this.videoSeekQueue.get(video) ?? Promise.resolve();
    const queued = previous
      .catch(() => undefined)
      .then(() => this.seekVideoNow(video, timeSeconds));
    const cleanup = queued.finally(() => {
      if (this.videoSeekQueue.get(video) === cleanup) {
        this.videoSeekQueue.delete(video);
      }
    });
    this.videoSeekQueue.set(video, cleanup);
    return cleanup;
  }

  private seekVideoNow(video: HTMLVideoElement, timeSeconds: number): Promise<void> {
    // Same Infinity-duration tolerance as playVideoPreview: a MediaRecorder WebM recording
    // must still be seekable in the paused preview — unknown duration just skips the clamp.
    const knownDuration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
    if (knownDuration === null && video.readyState < 1) return Promise.resolve();
    const targetTime = knownDuration === null
      ? Math.max(0, timeSeconds)
      : Math.max(0, Math.min(knownDuration - 0.05, timeSeconds));
    if (Math.abs(video.currentTime - targetTime) < 0.08) return Promise.resolve();
    return new Promise(resolve => {
      let resolved = false;
      const finish = () => {
        if (resolved) return;
        resolved = true;
        window.clearTimeout(timeout);
        video.removeEventListener('seeked', finish);
        resolve();
      };
      const timeout = window.setTimeout(finish, 200);
      video.addEventListener('seeked', finish, { once: true });
      video.currentTime = targetTime;
    });
  }

  /**
   * Wrap a media-frame blit with the per-clip video ops — crop (rect clip), shape mask, and color
   * adjust (ctx.filter + temp/vignette overlays). Called INSIDE withClipTransform (origin at the
   * canvas centre), so all ops are in the clip's transformed space and match the export worker. A
   * clip with none of these ops takes the fast path (byte-identical). Chroma-key is a separate pass.
   */
  private drawMediaFrame(context: CanvasRenderingContext2D, clip: EditorClip, W: number, H: number, timeSeconds: number, blit: (ctx: CanvasRenderingContext2D) => void, skipMasks = false): void {
    const masks = skipMasks ? [] : editorMasks(clip).map(mask => resolveMaskAt(mask, timeSeconds - clip.startSeconds));
    if (masks.length) {
      this.drawMaskedMediaFrame(context, clip, masks, W, H, timeSeconds, blit);
      return;
    }
    const adj = resolveAdjustAt(clip.adjust, clip.adjustKeyframes, timeSeconds - clip.startSeconds);
    const adjActive = adjustIsActive(adj);
    const hasMask = !!clip.mask;
    const hasCrop = !!clip.crop;
    const chroma = clip.chroma && clip.chroma.color ? clip.chroma : null;
    const resolvedBlurRegions = effectiveMotionTrackedBlurRegions(clip);
    const blurRegions = blurRegionsActive(resolvedBlurRegions) ? resolvedBlurRegions! : null;
    const enhancementCss = mediaEnhancementFilterCss(clip.mediaEnhancements, context.canvas.width / 1080);
    const motionBlurAlpha = mediaMotionBlurAlpha(clip.mediaEnhancements);
    const trackingBackground = mediaTrackingBackgroundBlur(clip.mediaEnhancements);
    if (!adjActive && !hasMask && !hasCrop && !chroma && !blurRegions && !enhancementCss && motionBlurAlpha <= 0 && !trackingBackground) { blit(context); return; }
    if (trackingBackground) {
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.translate(context.canvas.width / 2, context.canvas.height / 2);
      context.scale(1.08, 1.08);
      context.filter = `blur(${Math.max(12, Math.round(context.canvas.width / 70))}px)`;
      context.globalAlpha *= .82;
      blit(context);
      context.restore();
    }
    context.save();
    if (clip.mediaEnhancements?.enhanceQuality?.enabled) {
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
    }
    if (hasCrop) {
      const cr = clip.crop!;
      context.beginPath();
      context.rect(-W / 2 + cr.x * W, -H / 2 + cr.y * H, cr.width * W, cr.height * H);
      context.clip();
    }
    if (hasMask) applyMaskClip(context, clip.mask!, -W / 2, -H / 2, W, H);
    const adjustCss = adjActive && adj ? adjustFilterCss(adj, context.canvas.width / 1080) : '';
    context.filter = [adjustCss === 'none' ? '' : adjustCss, enhancementCss].filter(Boolean).join(' ') || 'none';
    if (chroma) {
      // CHROMA-KEY: draw the frame onto a transparent scratch (same transform baked in),
      // key the green out, then composite — keyed pixels reveal the layers beneath.
      const sctx = this.getChromaScratch(context.canvas.width, context.canvas.height);
      if (sctx) {
        sctx.save();
        sctx.setTransform(1, 0, 0, 1, 0, 0);
        sctx.clearRect(0, 0, sctx.canvas.width, sctx.canvas.height);
        sctx.setTransform(context.getTransform());
        blit(sctx);
        sctx.restore();
        applyChromaKey(sctx, chroma);
        const prev = context.getTransform();
        context.setTransform(1, 0, 0, 1, 0, 0);
        context.drawImage(sctx.canvas, 0, 0);
        context.setTransform(prev);
      } else {
        blit(context); // scratch unavailable → draw un-keyed rather than nothing
      }
    } else {
      blit(context);
    }
    if (motionBlurAlpha > 0) {
      const spec = clip.mediaEnhancements?.motionBlur;
      const copies = spec?.speed === '6x' ? 6 : spec?.speed === '4x' ? 4 : spec?.speed === 'twice' ? 2 : 1;
      const direction = spec?.direction ?? 'both';
      context.save();
      context.globalAlpha *= motionBlurAlpha / Math.max(1, copies * (direction === 'both' ? 2 : 1));
      for (let index = 1; index <= copies; index++) {
        const offset = index * Math.max(1, (spec?.blur ?? 0) / 18);
        if (direction !== 'backward') { context.save(); context.translate(offset, 0); blit(context); context.restore(); }
        if (direction !== 'forward') { context.save(); context.translate(-offset, 0); blit(context); context.restore(); }
      }
      context.restore();
    }
    context.filter = 'none';
    // BLUR/ERASE censor regions — after the frame (and its ctx.filter grade) is down, before
    // the temp/vignette overlays; the export worker applies them at the SAME point.
    if (blurRegions) {
      applyBlurRegions(context, blurRegions, -W / 2, -H / 2, W, H, context.canvas.width / 1080, this.blurScratchProvider, timeSeconds - clip.startSeconds);
    }
    if (adjActive && adj && adjustHasOverlay(adj)) paintAdjustOverlays(context, adj, -W / 2, -H / 2, W, H);
    context.restore();
  }

  /** Isolate one clip, apply the union/subtraction mask alpha, then composite it back. This
   * supports text and freehand stencils without ever cutting pixels from lower tracks. */
  private drawMaskedMediaFrame(
    context: CanvasRenderingContext2D,
    clip: EditorClip,
    masks: readonly ResolvedMask[],
    W: number,
    H: number,
    timeSeconds: number,
    blit: (ctx: CanvasRenderingContext2D) => void,
  ): void {
    const frame = this.getMaskScratch(context.canvas.width, context.canvas.height, 'frame');
    const alpha = this.getMaskScratch(context.canvas.width, context.canvas.height, 'alpha');
    if (!frame || !alpha) {
      // Canvas allocation failure: retain the legacy first-mask path as a safe fallback.
      const fallback = masks[0];
      this.drawMediaFrame(context, { ...clip, masks: null, mask: fallback ?? null }, W, H, timeSeconds, blit, true);
      return;
    }
    const transform = context.getTransform();
    const sourceAlpha = context.globalAlpha;
    const blend = context.globalCompositeOperation;
    this.clearScratch(frame);
    this.clearScratch(alpha);

    frame.setTransform(transform);
    frame.globalAlpha = sourceAlpha;
    frame.globalCompositeOperation = 'source-over';
    this.drawMediaFrame(frame, { ...clip, mask: null, masks: null }, W, H, timeSeconds, blit, true);

    alpha.setTransform(transform);
    paintMaskAlpha(alpha, masks, -W / 2, -H / 2, W, H);
    frame.save();
    frame.setTransform(1, 0, 0, 1, 0, 0);
    frame.globalAlpha = 1;
    frame.globalCompositeOperation = 'destination-in';
    frame.drawImage(alpha.canvas, 0, 0);
    frame.restore();

    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.globalAlpha = 1;
    context.globalCompositeOperation = blend;
    context.drawImage(frame.canvas, 0, 0);
    context.restore();

    const matte = [...masks].reverse().find(mask => !!mask.fillColor && mask.fillOpacity > 0);
    if (matte?.fillColor) {
      this.clearScratch(frame);
      frame.setTransform(transform);
      frame.fillStyle = matte.fillColor;
      frame.fillRect(-W / 2, -H / 2, W, H);
      frame.save();
      frame.setTransform(1, 0, 0, 1, 0, 0);
      frame.globalCompositeOperation = 'destination-out';
      frame.drawImage(alpha.canvas, 0, 0);
      frame.restore();
      context.save();
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.globalAlpha = matte.fillOpacity;
      context.globalCompositeOperation = blend;
      context.drawImage(frame.canvas, 0, 0);
      context.restore();
    }
  }

  private maskScratchCtxs: { frame: CanvasRenderingContext2D | null; alpha: CanvasRenderingContext2D | null } = { frame: null, alpha: null };
  private getMaskScratch(width: number, height: number, key: 'frame' | 'alpha'): CanvasRenderingContext2D | null {
    let ctx = this.maskScratchCtxs[key];
    if (!ctx) { const canvas = document.createElement('canvas'); ctx = canvas.getContext('2d'); this.maskScratchCtxs[key] = ctx; }
    if (!ctx) return null;
    if (ctx.canvas.width !== width) ctx.canvas.width = width;
    if (ctx.canvas.height !== height) ctx.canvas.height = height;
    return ctx;
  }
  private clearScratch(ctx: CanvasRenderingContext2D): void { ctx.save(); ctx.setTransform(1,0,0,1,0,0); ctx.globalAlpha=1; ctx.globalCompositeOperation='source-over'; ctx.filter='none'; ctx.clearRect(0,0,ctx.canvas.width,ctx.canvas.height); ctx.restore(); }

  /** Persistent censor scratch pair (full-size censored copy + tiny pixelate intermediate). */
  private blurScratchCtxs: { full: CanvasRenderingContext2D | null; small: CanvasRenderingContext2D | null } = { full: null, small: null };
  private readonly blurScratchProvider = (width: number, height: number, key: 'full' | 'small'): CanvasRenderingContext2D | null => {
    let ctx = this.blurScratchCtxs[key];
    if (!ctx) {
      const canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d');
      this.blurScratchCtxs[key] = ctx;
    }
    if (!ctx) return null;
    if (ctx.canvas.width !== width) ctx.canvas.width = width;
    if (ctx.canvas.height !== height) ctx.canvas.height = height;
    return ctx;
  };

  /** Persistent chroma scratch (willReadFrequently — applyChromaKey does getImageData per frame). */
  private getChromaScratch(width: number, height: number): CanvasRenderingContext2D | null {
    if (!this.chromaScratchCtx) {
      const canvas = document.createElement('canvas');
      this.chromaScratchCtx = canvas.getContext('2d', { willReadFrequently: true });
    }
    const ctx = this.chromaScratchCtx;
    if (!ctx) return null;
    if (ctx.canvas.width !== width) ctx.canvas.width = width;
    if (ctx.canvas.height !== height) ctx.canvas.height = height;
    return ctx;
  }

  private drawCover(
    context: CanvasRenderingContext2D,
    source: CanvasImageSource,
    width: number,
    height: number,
    x = 0,
    y = 0,
    fit: 'cover' | 'contain' | 'stretch' = 'cover'
  ): void {
    const sourceWidth = source instanceof HTMLVideoElement ? source.videoWidth : (source as HTMLImageElement).naturalWidth;
    const sourceHeight = source instanceof HTMLVideoElement ? source.videoHeight : (source as HTMLImageElement).naturalHeight;
    if (!sourceWidth || !sourceHeight) return;
    if (fit === 'stretch') {
      context.drawImage(source, x, y, width, height);
      return;
    }
    const scale = fit === 'contain'
      ? Math.min(width / sourceWidth, height / sourceHeight)
      : Math.max(width / sourceWidth, height / sourceHeight);
    const drawWidth = sourceWidth * scale;
    const drawHeight = sourceHeight * scale;
    context.drawImage(source, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
  }

  /**
   * Cover-fit a decoded mediabunny frame into the canvas, using the SAME math as
   * drawCover (Math.max scale, centered) so a mediabunny-sourced clip lands pixel-for-pixel
   * where the <video>-sourced one did. The drawable's own draw() does the scaled blit.
   */
  private drawCoverDrawable(
    context: CanvasRenderingContext2D,
    frame: { width: number; height: number; draw: (ctx: CanvasRenderingContext2D, dx: number, dy: number, dw: number, dh: number) => void },
    width: number,
    height: number,
    x = 0,
    y = 0,
    fit: 'cover' | 'contain' | 'stretch' = 'cover'
  ): void {
    const sourceWidth = frame.width;
    const sourceHeight = frame.height;
    if (!sourceWidth || !sourceHeight) return;
    if (fit === 'stretch') {
      frame.draw(context, x, y, width, height);
      return;
    }
    const scale = fit === 'contain'
      ? Math.min(width / sourceWidth, height / sourceHeight)
      : Math.max(width / sourceWidth, height / sourceHeight);
    const drawWidth = sourceWidth * scale;
    const drawHeight = sourceHeight * scale;
    frame.draw(context, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight);
  }

  private drawAssetFallback(context: CanvasRenderingContext2D, canvas: HTMLCanvasElement, asset: EditorAsset): void {
    context.save();
    context.fillStyle = 'rgba(255, 255, 255, 0.08)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = 'rgba(255, 255, 255, 0.72)';
    context.font = `${Math.max(14, Math.round(canvas.width / 42))}px system-ui`;
    context.textAlign = 'center';
    context.fillText(asset.name, canvas.width / 2, canvas.height / 2);
    context.restore();
  }

  private roundRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
    const r = Math.min(radius, width / 2, height / 2);
    context.beginPath();
    context.moveTo(x + r, y);
    context.arcTo(x + width, y, x + width, y + height, r);
    context.arcTo(x + width, y + height, x, y + height, r);
    context.arcTo(x, y + height, x, y, r);
    context.arcTo(x, y, x + width, y, r);
    context.closePath();
  }

  private isActive(clip: EditorClip, timeSeconds: number): boolean {
    // Use exclusive end so two sequential clips (clip1.end === clip2.start) are never
    // both active on the same frame — clip1 yields exactly at its endSeconds.
    return clipContainsTime(clip, timeSeconds);
  }

  private withClipTransform(
    context: CanvasRenderingContext2D,
    clip: EditorClip,
    baseX: number,
    baseY: number,
    timeSeconds: number,
    draw: () => void,
    motion?: CombinedMotionDelta,
    allowOutsideCanvas = false,
  ): void {
    const resolved = this.resolveCanvasClipTransform(
      clip,
      timeSeconds,
      context.canvas.width,
      context.canvas.height,
      motion,
      allowOutsideCanvas,
    );

    context.save();
    context.globalAlpha *= resolved.opacity;
    context.globalCompositeOperation = mediaBlendOperation(clip.mediaEnhancements);
    context.translate(baseX + resolved.offsetX, baseY + resolved.offsetY);
    context.rotate((resolved.rotation * Math.PI) / 180);
    context.scale(resolved.scaleX, resolved.scaleY);
    draw();
    context.restore();
  }

  /**
   * Resolve the complete visual transform once for both painting and selection geometry.
   * This includes authored keyframes, tracking samples, enter/exit presets, legacy keyword
   * motion, and optional Animation-lane motion composition.
   */
  private resolveCanvasClipTransform(
    clip: EditorClip,
    timeSeconds: number,
    canvasWidth: number,
    canvasHeight: number,
    motion?: CombinedMotionDelta,
    allowOutsideCanvas = false,
  ): ResolvedCanvasClipTransform {
    const transform = clip.transform;
    const duration = Math.max(0.1, clip.endSeconds - clip.startSeconds);
    const progress = this.clampNumber((timeSeconds - clip.startSeconds) / duration, 0, 1);
    // KEYFRAME + ENTER/EXIT resolve (the SHARED resolver — the SAME one the worker
    // compositor calls). Its fast path returns the base channels unchanged + an
    // identity delta when the clip has no keyframes and no animateIn/Out, so a plain
    // clip is byte-identical to before with no per-frame allocation.
    const resolved = resolveTransformAt(
      {
        baseTransform: transform,
        keyframes: effectiveMotionTrackingKeyframes(clip),
        animateIn: clip.animateIn,
        animateOut: clip.animateOut,
        clipStartSeconds: clip.startSeconds,
        clipEndSeconds: clip.endSeconds
      },
      timeSeconds
    );
    const delta = resolved.delta;

    // Legacy keyword animation (metadata-driven drift/slide/zoom/fade) is preserved
    // and now routed through the shared kinematics so the sticker path and clip
    // animations can never diverge. motionOffsetNorm returns NORMALIZED offsets.
    const animation = this.clipAnimationName(clip);
    const kw = motionOffsetNorm(animation, progress);
    const motionX = kw.x * canvasWidth;
    const motionY = kw.y * canvasHeight;
    const zoom = animationZoom(animation, progress);
    const alpha = this.transitionAlpha(clip, progress);

    // MOTION-clip fold (kind:'motion' on 'animation' tracks): the combined delta from
    // every active motion clip, composed onto THIS (base) clip's resolved channels — the
    // EXACT same fold the worker's `applyMotionToResolved` does (additive percent offset +
    // rotation, multiplicative uniform `scale` × pure per-axis `scaleX`/`scaleY`, ×opacity),
    // so preview == export. Inactive / absent ⇒ neutral (identity), leaving the clip
    // byte-for-byte as today.
    const m: CombinedMotionDelta = motion && motion.active ? motion
      : { active: false, x: 0, y: 0, scale: 1, scaleX: 1, scaleY: 1, rotation: 0, opacity: 1 };

    // Resolved channels are in the editor's native units: x/y are PERCENT offsets,
    // scale is a multiplier, rotation deg, opacity 0..1. Fold the normalized enter/
    // exit delta in: dx/dyNorm shift position, scaleMul multiplies scale, opacityMul
    // multiplies alpha. The motion delta adds onto each channel the same way.
    const stabilizationOffset = mediaStabilizationOffset(clip.mediaEnhancements, timeSeconds - clip.startSeconds);
    const offsetX = ((resolved.x + m.x + stabilizationOffset.x) / 100) * canvasWidth + delta.dxNorm * canvasWidth;
    const offsetY = ((resolved.y + m.y + stabilizationOffset.y) / 100) * canvasHeight + delta.dyNorm * canvasHeight;
    // Per-axis scale already resolved (scaleX/scaleY fall back to scale exactly like
    // effectiveScaleXY); apply the enter/exit scale multiplier AND the motion scale on top.
    const stabilizationScale = mediaStabilizationScale(clip.mediaEnhancements);
    // The inspector and on-canvas handles intentionally have no upper scale limit.
    // Keep only a positive minimum so a malformed transform cannot invert the canvas.
    const scaleX = Math.max(0.1, resolved.scaleX * delta.scaleMul * m.scale * m.scaleX * stabilizationScale);
    const scaleY = Math.max(0.1, resolved.scaleY * delta.scaleMul * m.scale * m.scaleY * stabilizationScale);
    const opacity = this.clampNumber(resolved.opacity * delta.opacityMul * m.opacity, 0, 1);
    const rotation = resolved.rotation + m.rotation + (animation.includes('rotate') ? progress * 4 : 0);

    const visualOffsetX = offsetX + motionX;
    const visualOffsetY = offsetY + motionY;
    const visualScaleX = scaleX * zoom;
    const visualScaleY = scaleY * zoom;
    return {
      offsetX: visualOffsetX,
      offsetY: visualOffsetY,
      scaleX: visualScaleX,
      scaleY: visualScaleY,
      rotation,
      opacity: opacity * alpha,
    };
  }

  private playVideoPreview(
    video: HTMLVideoElement,
    targetTime: number,
    playbackRate: number | undefined,
    volume: number,
    audioCleanup?: EditorClip['audioCleanup'],
  ): void {
    // MediaRecorder WebM (camera/screen recordings) reports duration = Infinity on a <video>
    // element — that must NOT block playback (bailing out here left a recording's element
    // paused forever, pinning the master clock at the clip's start). An unknown duration only
    // means "don't clamp the seek target"; bail out only while metadata hasn't loaded at all.
    const knownDuration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
    if (knownDuration === null && video.readyState < 1) return;
    const safeTime = knownDuration === null
      ? Math.max(0, targetTime)
      : Math.max(0, Math.min(knownDuration - 0.05, targetTime));
    const state = this.videoPlaybackState.get(video);
    const shouldSeek = shouldSeekPreviewVideoElement(state, safeTime, video.currentTime, true);
    if (shouldSeek) {
      const count = Number(video.getAttribute('data-jivu-preview-seek-count') || '0') + 1;
      video.setAttribute('data-jivu-preview-seek-count', String(count));
      video.setAttribute('data-jivu-preview-last-seek', `${Math.round(video.currentTime * 100) / 100}->${Math.round(safeTime * 100) / 100}`);
      video.currentTime = safeTime;
    }
    const desiredRate = Number.isFinite(playbackRate) && playbackRate && playbackRate > 0 ? playbackRate : 1;
    // Only write playbackRate when it actually changes — writing it every draw frame is part
    // of the per-frame churn that de-paces the media element.
    if (Math.abs(video.playbackRate - desiredRate) > 0.001) video.playbackRate = desiredRate;
    const safeVolume = Math.max(0, Math.min(2, volume));
    if (shouldSeek) this.previewCleanup.reset(video, audioCleanup, safeVolume);
    else this.previewCleanup.apply(video, audioCleanup, safeVolume);
    this.videoDesiredVolume.set(video, safeVolume);
    if (video.paused) {
      video.muted = true;
      video.volume = 0;
      video.setAttribute('data-jivu-preview-play-request', 'preview');
      void video.play()
        .then(() => {
          video.setAttribute('data-jivu-preview-play-result', 'preview-ok');
          this.applyDesiredVideoVolume(video);
        })
        .catch(error => video.setAttribute('data-jivu-preview-play-result', `preview-error:${error?.name || 'error'}`));
    } else {
      this.applyDesiredVideoVolume(video);
    }
    this.videoPlaybackState.set(video, { targetTime: safeTime, playing: true });
  }

  private applyDesiredVideoVolume(video: HTMLVideoElement): void {
    const desiredVolume = Math.max(0, Math.min(2, this.videoDesiredVolume.get(video) ?? 0));
    // Only touch volume/muted when they actually change. This runs on every draw frame;
    // re-writing volume + toggling muted each frame thrashes the audio renderer and breaks
    // the media element's real-time pacing (the ~3x-fast + stutter bug).
    const elementVolume = desiredVolume > 0 ? 1 : 0;
    if (Math.abs(video.volume - elementVolume) > 0.001) video.volume = elementVolume;
    const desiredMuted = desiredVolume <= 0;
    if (video.muted !== desiredMuted) video.muted = desiredMuted;
  }

  private buildPreviewMediaClock(
    projectId: string,
    clip: EditorClip,
    video: HTMLVideoElement,
    clipRate: number
  ): PreviewMediaClock | null {
    const safeRate = clipRate > 0 ? clipRate : 1;
    const sourceStart = clip.sourceStartSeconds || 0;
    const sourceTime = Math.max(0, video.currentTime || 0);
    let timelineTime = clip.startSeconds + Math.max(0, sourceTime - sourceStart) / safeRate;
    // A user scrub/seek made while playing: hold the clock at the requested target until the
    // <video> actually reaches it, then resume following the element. Without this hold the
    // clock follows the element's mid-seek position and the playhead/video oscillate.
    if (this.pendingSeekTimelineTime !== null) {
      const targetSource = (this.pendingSeekTimelineTime - clip.startSeconds) * safeRate + sourceStart;
      const reached = !video.seeking && Math.abs(sourceTime - targetSource) <= 0.35;
      if (reached) {
        this.pendingSeekTimelineTime = null;       // element arrived — resume natural following
      } else {
        timelineTime = this.pendingSeekTimelineTime; // hold here while the element seeks there
      }
    }
    // The source element FINISHED (a source shorter than its clip window — e.g. a recording,
    // or a trailing cut segment): its currentTime freezes, which would pin the playhead just
    // below the clip end forever. Release the clock AT the clip end so the next active clip
    // takes over as master (or the end-of-timeline loop wraps) and playback continues to the
    // end of ALL elements.
    //
    // Release ONLY when (a) no scrub/loop-wrap hold is pending — the wrap just seeked the
    // element back, and releasing would snap the playhead to this clip's end ("loop restarts
    // from the cut instead of 0") — and (b) the element is REALLY at its source end right
    // now: `ended` stays latched on a paused/just-reseeked element for a beat (master
    // hand-off onto a source that finished an earlier pass), and an unguarded release
    // snapped the playhead to the cut and pinned it there while the source caught up.
    const knownSourceDuration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
    const atSourceEndNow = knownSourceDuration !== null && sourceTime >= knownSourceDuration - 0.1;
    if (video.ended && atSourceEndNow && this.pendingSeekTimelineTime === null) {
      timelineTime = Math.max(clip.startSeconds, clip.endSeconds);
    }

    // ── Stalled-master WATCHDOG ──────────────────────────────────────────────────────────
    // A MediaRecorder WebM (camera/screen recording) reports duration = Infinity, and Chrome
    // treats it as a LIVE stream: at the real end of its data the element just freezes with
    // paused=false and NEVER fires 'ended' — so the release above can't catch it and the
    // frozen mapped time would pin the playhead ("stuck on the last media, never restarts").
    // Track per-element source progress: no movement for >450ms while genuinely playing (not
    // seeking, no scrub hold, not paused) means the source is done — for an unknown-duration
    // source (local blob; can't be "buffering") or one at its known end, release AT the clip
    // end exactly like `ended`. A mid-stream stall of a known-duration source (remote
    // buffering) YIELDS the clock instead (null → the playback clock holds briefly, then its
    // wait-media grace advances) rather than skipping content.
    const nowMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const progress = this.videoProgressWatch.get(video);
    if (!progress || Math.abs(progress.sourceTime - sourceTime) > 0.005) {
      this.videoProgressWatch.set(video, { sourceTime, changedAtMs: nowMs });
    } else if (!video.ended && !video.seeking && !video.paused && this.pendingSeekTimelineTime === null) {
      const stalledMs = nowMs - progress.changedAtMs;
      if (stalledMs > 450) {
        const knownDuration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
        const atSourceEnd = knownDuration === null || sourceTime >= knownDuration - 0.3;
        if (atSourceEnd) {
          timelineTime = Math.max(clip.startSeconds, clip.endSeconds);
        } else {
          return null;
        }
      }
    }
    const generatedAtMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return {
      projectId,
      clipId: clip.id,
      timelineTimeSeconds: this.clampNumber(timelineTime, clip.startSeconds, Math.max(clip.startSeconds, clip.endSeconds)),
      sourceTimeSeconds: sourceTime,
      mediaDurationSeconds: Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null,
      paused: video.paused,
      generatedAtMs
    };
  }

  private pauseInactiveVideos(activeUrls: Set<string>, activeVideos: Set<HTMLVideoElement>, isPlaying: boolean): void {
    if (!this.videoCache.size) return;
    for (const [url, videoPromise] of this.videoCache.entries()) {
      if (isPlaying && activeUrls.has(url)) continue;
      void videoPromise.then(video => {
        if (isPlaying && activeVideos.has(video)) return;
        if (!video.paused) {
          video.setAttribute('data-jivu-preview-paused-by', 'inactive-cleanup');
          video.pause();
        }
        video.muted = true;
        this.videoPlaybackState.set(video, { targetTime: video.currentTime || 0, playing: false });
      }).catch(() => undefined);
    }
  }

  private clipAnimationName(clip: EditorClip): string {
    const metadata = clip.metadata ?? {};
    return this.normalize([
      metadata['animation'],
      metadata['effect'],
      metadata['transition']
    ].filter(Boolean).join('_'));
  }

  private transitionAlpha(clip: EditorClip, progress: number): number {
    const transition = this.normalize(clip.metadata?.['transition']);
    if (!transition.includes('fade') && !transition.includes('crossfade')) return 1;
    const edge = 0.18;
    if (progress < edge) return this.clampNumber(progress / edge, 0, 1);
    if (progress > 1 - edge) return this.clampNumber((1 - progress) / edge, 0, 1);
    return 1;
  }

  private trimImageCache(): void {
    while (this.imageCache.size > this.maxImageCacheEntries) {
      const next = this.imageCache.keys().next();
      if (next.done) return;
      this.imageCache.delete(next.value);
    }
  }

  private trimVideoCache(): void {
    while (this.videoCache.size > this.maxVideoCacheEntries) {
      const next = this.videoCache.entries().next();
      if (next.done) return;
      const [url, videoPromise] = next.value;
      this.videoCache.delete(url);
      this.videoReadyKeys.delete(url);
      void videoPromise.then(video => this.releaseVideo(video)).catch(() => undefined);
    }
  }

  private releaseVideo(video: HTMLVideoElement): void {
    this.previewVideos.delete(video);
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.remove();
  }

  private attachVideoElement(video: HTMLVideoElement): void {
    const host = this.ensureVideoHost();
    if (!host || video.parentElement === host) return;
    host.appendChild(video);
  }

  private ensureVideoHost(): HTMLElement | null {
    if (this.videoHost?.isConnected) return this.videoHost;
    if (typeof document === 'undefined') return null;
    const existing = document.querySelector<HTMLElement>('[data-jivu-editor-preview-video-host]');
    if (existing) {
      this.videoHost = existing;
      return existing;
    }
    const host = document.createElement('div');
    host.setAttribute('data-jivu-editor-preview-video-host', 'true');
    host.style.position = 'fixed';
    host.style.left = '-9999px';
    host.style.top = '-9999px';
    host.style.width = '1px';
    host.style.height = '1px';
    host.style.overflow = 'hidden';
    host.style.opacity = '0';
    host.style.pointerEvents = 'none';
    document.body.appendChild(host);
    this.videoHost = host;
    return host;
  }

  /**
   * Returns the canvas-internal-pixel bounding box for every ACTIVE selectable visual clip
   * (video, image, text, caption, sticker) at the given time. Reuses the EXACT same center+offset+scale
   * math used by the draw path so hit-testing matches rendering 1:1.
   *
   * "Selectable" = video, image, text, caption, sticker (not audio, effect, transition, beat).
   *
   * @param project  The current EditorProject.
   * @param timeSeconds  Current playhead time.
   * @param canvas  The canvas element (used for its .width / .height).
   * @returns Array of clip rects in canvas-internal px. cx/cy is the visual centre point.
   */
  getActiveClipRects(
    project: EditorProject,
    timeSeconds: number,
    canvas: HTMLCanvasElement,
    options: Pick<CanvasPreviewOptions, 'isPlaying' | 'selectedClipId'> = {},
  ): ClipRect[] {
    const cw = canvas.width;
    const ch = canvas.height;
    const rects: ClipRect[] = [];

    // Skip clips on hidden tracks — a hidden clip is not drawn, so it must not be hit-testable.
    for (const track of project.tracks) {
      if (track.hidden) continue;
      for (const clip of track.clips) {
      if (
        !this.isActive(clip, timeSeconds)
        && !(
          clip.kind === 'text'
          && clip.textDesign
          && shouldDrawTextDesignInPreview(
            clip,
            timeSeconds,
            Boolean(options.isPlaying),
            options.selectedClipId,
          )
        )
      ) continue;
      // Rects are emitted in paint order and the overlay hit-test walks them in reverse,
      // so text/PiP layers win while the base video owns the otherwise-empty painted frame.
      // That keeps a timeline-selected video selected and directly draggable on the canvas.
      if (!SELECTABLE_KINDS.has(clip.kind)) continue;

      const geometryClip = clip.kind === 'text'
        && clip.textDesign
        && shouldRevealSelectedTextDesignAtStart(
          clip,
          timeSeconds,
          Boolean(options.isPlaying),
          options.selectedClipId,
        )
          ? { ...clip, animateIn: null }
          : clip;
      const visualTransform = this.resolveCanvasClipTransform(
        geometryClip,
        timeSeconds,
        cw,
        ch,
        undefined,
        track.kind === 'overlay',
      );
      const offsetX = visualTransform.offsetX;
      const offsetY = visualTransform.offsetY;
      // Per-axis scale (shared helper) so the selection box + handles track a
      // non-uniformly resized overlay. Uniform clips → sx === sy === scale.
      const scaleX = visualTransform.scaleX;
      const scaleY = visualTransform.scaleY;
      const rotation = visualTransform.rotation;

      // Base centre before transform: same as withClipTransform's translate target
      let baseX = cw / 2;
      let baseY = ch / 2;

      // Ordinary text / captions use a lower-third default. Text Designs always render
      // in the full-composition coordinate space and therefore stay canvas-centred.
      if ((clip.kind === 'text' || clip.kind === 'caption') && !clip.textDesign && !clip.transform) {
        baseY = ch * 0.78;
      }

      // Approximate bounding box size in canvas-internal px (pre-scale so we can apply scale after)
      let rawW: number;
      let rawH: number;
      let localCenterX = 0;
      let localCenterY = 0;

      if (clip.kind === 'sticker') {
        const size = this.clampNumber(clip.sticker?.size ?? 0.5, 0.1, 1);
        // Largest sticker primitive is the label-strip: ~(0.4*cw + 1.6*fontPx) × ~1.7*fontPx
        const fontPx = Math.max(20, Math.round(cw * (0.03 + size * 0.04)));
        rawW = Math.max(cw * 0.4, cw * 0.2 + fontPx * 1.6);
        rawH = fontPx * 2.0;
      } else if (clip.kind === 'text' || clip.kind === 'caption') {
        if (clip.textDesign) {
          // Select only the union of painted declarative layer frames. A full-canvas
          // rect made every design swallow clicks intended for media underneath it.
          const bounds = resolveTextDesignSelectionBounds(
            withoutUnwantedTextDesignSurface(clip.textDesign),
            cw,
            ch,
          );
          if (!bounds) continue;
          rawW = bounds.width * cw;
          rawH = bounds.height * ch;
          localCenterX = (bounds.centerX - 0.5) * cw;
          localCenterY = (bounds.centerY - 0.5) * ch;
        } else {
          const bounds = this.measureTextClipSelectionBounds(canvas, clip);
          rawW = bounds.width;
          rawH = bounds.height;
          localCenterX = bounds.centerX;
          localCenterY = bounds.centerY;
        }
      } else {
        // Match the exact visible media surface. A contained asset can be smaller
        // than the canvas, and Crop clips that surface before it is painted.
        const mediaBounds = this.mediaClipSelectionBounds(project, clip, cw, ch);
        rawW = mediaBounds.width;
        rawH = mediaBounds.height;
        localCenterX = mediaBounds.centerX;
        localCenterY = mediaBounds.centerY;
      }

      const w = rawW * scaleX;
      const h = rawH * scaleY;

      // A design's layer union need not be centred in its full-canvas local viewport.
      // Apply the clip's scale + rotation to that local centre exactly as the canvas
      // transform does before exposing it to CanvasOverlay hit testing.
      const radians = rotation * Math.PI / 180;
      const scaledLocalX = localCenterX * scaleX;
      const scaledLocalY = localCenterY * scaleY;
      const cx = baseX + offsetX
        + scaledLocalX * Math.cos(radians)
        - scaledLocalY * Math.sin(radians);
      const cy = baseY + offsetY
        + scaledLocalX * Math.sin(radians)
        + scaledLocalY * Math.cos(radians);

      rects.push({ clipId: clip.id, kind: clip.kind, cx, cy, w, h, rotationDeg: rotation });
      }
    }

    return rects;
  }

  /** Visible local-space media bounds, mirroring drawCover + drawMediaFrame crop. */
  private mediaClipSelectionBounds(
    project: EditorProject,
    clip: EditorClip,
    canvasWidth: number,
    canvasHeight: number,
  ): { width: number; height: number; centerX: number; centerY: number } {
    let left = -canvasWidth / 2;
    let top = -canvasHeight / 2;
    let right = canvasWidth / 2;
    let bottom = canvasHeight / 2;

    if (clip.fitMode === 'contain' && clip.assetId) {
      const asset = project.assets.find(item => item.id === clip.assetId);
      const sourceWidth = Number(asset?.width);
      const sourceHeight = Number(asset?.height);
      if (sourceWidth > 0 && sourceHeight > 0) {
        const fitScale = Math.min(canvasWidth / sourceWidth, canvasHeight / sourceHeight);
        const fittedWidth = sourceWidth * fitScale;
        const fittedHeight = sourceHeight * fitScale;
        left = -fittedWidth / 2;
        right = fittedWidth / 2;
        top = -fittedHeight / 2;
        bottom = fittedHeight / 2;
      }
    }

    if (clip.crop) {
      const cropLeft = -canvasWidth / 2 + clip.crop.x * canvasWidth;
      const cropTop = -canvasHeight / 2 + clip.crop.y * canvasHeight;
      const cropRight = cropLeft + clip.crop.width * canvasWidth;
      const cropBottom = cropTop + clip.crop.height * canvasHeight;
      left = Math.max(left, cropLeft);
      top = Math.max(top, cropTop);
      right = Math.min(right, cropRight);
      bottom = Math.min(bottom, cropBottom);
    }

    const width = Math.max(1, right - left);
    const height = Math.max(1, bottom - top);
    return {
      width,
      height,
      centerX: left + width / 2,
      centerY: top + height / 2,
    };
  }

  /** Measure the actual painted text, including curve, glow, shadow, stroke, and letter motion. */
  private measureTextClipSelectionBounds(
    canvas: HTMLCanvasElement,
    clip: EditorClip,
  ): { width: number; height: number; centerX: number; centerY: number } {
    const ts = clip.textStyle ?? {};
    const referenceScale = canvas.width / 1080;
    const referencePx = (ts.fontSize ?? 48) * this.positiveOr(ts.sizePercent, 1);
    const fontPx = Math.max(12, Math.round(referencePx * referenceScale));
    const lineHeight = Math.max(fontPx, fontPx * this.clampNumber(ts.lineHeight ?? 1.25, 0.8, 2));
    const text = String(clip.text || clip.title || 'Text');
    const maxWidth = canvas.width * 0.84;
    const context = typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null;
    let lines = text.split(/\r?\n/);
    let widths = lines.map(line => Math.min(maxWidth, Math.max(fontPx, Array.from(line).length * fontPx * 0.58)));

    if (context) {
      context.save();
      const weight = typeof ts.fontWeight === 'number' ? ts.fontWeight : 700;
      context.font = `${ts.fontStyle ?? 'normal'} ${weight} ${fontPx}px ${this.captionFontFamily(ts)}`;
      lines = this.wrapCaptionLines(context, text, maxWidth);
      widths = lines.map(line => Math.max(fontPx * 0.4, context.measureText(line).width));
      context.restore();
    }

    const blockH = Math.max(lineHeight, lines.length * lineHeight);
    const firstY = -(blockH - lineHeight) / 2;
    const align = ts.align ?? 'center';
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (let index = 0; index < lines.length; index++) {
      const width = widths[index] ?? fontPx;
      const lineY = firstY + index * lineHeight;
      if (Number.isFinite(ts.curve) && Math.abs(ts.curve ?? 0) >= 0.5 && lines[index]!.length > 1) {
        const curve = curvedTextBounds(width, fontPx, ts.curve);
        // drawCurvedTextLine builds its circle around the line midpoint. Relative to
        // the authored anchor that midpoint is +width/2 for left alignment, 0 for
        // center, and -width/2 for right alignment. Keep the box on that same centre.
        const alignmentOffsetX = align === 'left' ? width / 2 : align === 'right' ? -width / 2 : 0;
        minX = Math.min(minX, alignmentOffsetX + curve.centerOffsetX - curve.width / 2);
        maxX = Math.max(maxX, alignmentOffsetX + curve.centerOffsetX + curve.width / 2);
        minY = Math.min(minY, lineY + curve.centerOffsetY - curve.height / 2);
        maxY = Math.max(maxY, lineY + curve.centerOffsetY + curve.height / 2);
      } else {
        const left = align === 'left' ? 0 : align === 'right' ? -width : -width / 2;
        minX = Math.min(minX, left);
        maxX = Math.max(maxX, left + width);
        minY = Math.min(minY, lineY - fontPx * 0.58);
        maxY = Math.max(maxY, lineY + fontPx * 0.58);
      }
    }

    if (!Number.isFinite(minX)) {
      minX = -fontPx;
      maxX = fontPx;
      minY = -fontPx * 0.65;
      maxY = fontPx * 0.65;
    }

    // Backgrounds and bubbles are deliberately larger than the glyph block.
    if (ts.bubble) {
      const paddingX = fontPx * 0.45;
      const paddingY = fontPx * 0.34;
      minX -= paddingX;
      maxX += paddingX;
      minY -= paddingY;
      maxY += paddingY;
    } else if (ts.background) {
      const widest = widths.reduce((max, width) => Math.max(max, width), 0);
      const bx = align === 'center' ? -widest / 2 : align === 'right' ? -widest : 0;
      const box = resolveTextBackgroundBox(
        bx - fontPx * 0.4,
        firstY - lineHeight / 2 - fontPx * 0.28,
        widest + fontPx * 0.8,
        blockH + fontPx * 0.56,
        fontPx,
        referenceScale,
        {
          roundness: ts.backgroundRoundness,
          width: ts.backgroundWidth,
          height: ts.backgroundHeight,
          offsetX: ts.backgroundOffsetX,
          offsetY: ts.backgroundOffsetY,
        },
      );
      minX = Math.min(minX, box.x);
      maxX = Math.max(maxX, box.x + box.width);
      minY = Math.min(minY, box.y);
      maxY = Math.max(maxY, box.y + box.height);
    }

    const strokeColor = ts.stroke === undefined ? 'rgba(0, 0, 0, 0.62)' : ts.stroke;
    if (strokeColor) {
      const strokeWidth = typeof ts.strokeWidth === 'number'
        ? Math.max(0.5, ts.strokeWidth * referenceScale)
        : Math.max(fontPx * 0.14, canvas.width * 0.006);
      const padding = strokeWidth / 2;
      minX -= padding;
      maxX += padding;
      minY -= padding;
      maxY += padding;
    }

    const effectBaseMinX = minX;
    const effectBaseMaxX = maxX;
    const effectBaseMinY = minY;
    const effectBaseMaxY = maxY;

    const glow = resolveTextGlow(ts.glow, fontPx, referenceScale);
    if (glow) {
      const padding = glow.blurPx * 1.6 + 2;
      minX = Math.min(minX, effectBaseMinX + glow.offsetX - padding);
      maxX = Math.max(maxX, effectBaseMaxX + glow.offsetX + padding);
      minY = Math.min(minY, effectBaseMinY + glow.offsetY - padding);
      maxY = Math.max(maxY, effectBaseMaxY + glow.offsetY + padding);
    }

    const shadow = resolveTextShadow(ts.shadow, fontPx, referenceScale);
    if (shadow) {
      const padding = shadow.blurPx * 1.6 + 2;
      minX = Math.min(minX, effectBaseMinX + shadow.offsetX - padding);
      maxX = Math.max(maxX, effectBaseMaxX + shadow.offsetX + padding);
      minY = Math.min(minY, effectBaseMinY + shadow.offsetY - padding);
      maxY = Math.max(maxY, effectBaseMaxY + shadow.offsetY + padding);
    }

    if (ts.shadow3d?.color) {
      const dx = (ts.shadow3d.dx ?? 0) * referenceScale;
      const dy = typeof ts.shadow3d.dy === 'number' ? ts.shadow3d.dy * referenceScale : fontPx * 0.09;
      minX = Math.min(minX, effectBaseMinX + dx);
      maxX = Math.max(maxX, effectBaseMaxX + dx);
      minY = Math.min(minY, effectBaseMinY + dy);
      maxY = Math.max(maxY, effectBaseMaxY + dy);
    }

    // Letter animations can temporarily rise, wave, or overshoot their static glyph box.
    if (ts.textAnim?.type) {
      const padding = fontPx * 0.7;
      minX -= padding;
      maxX += padding;
      minY -= padding;
      maxY += padding;
    }

    return {
      width: Math.max(24, maxX - minX),
      height: Math.max(24, maxY - minY),
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
    };
  }

  private clampNumber(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
  }

  private normalize(value: unknown): string {
    return String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  }
}

/** Clip kinds that are directly selectable on the canvas overlay. */
const SELECTABLE_KINDS = new Set<string>(['video', 'text', 'caption', 'sticker', 'image']);

/** A clip's canvas-internal bounding box, suitable for hit-testing and overlay rendering. */
export interface ClipRect {
  clipId: string;
  kind: string;
  /** Centre X in canvas-internal pixels. */
  cx: number;
  /** Centre Y in canvas-internal pixels. */
  cy: number;
  /** Full width in canvas-internal pixels (after scale). */
  w: number;
  /** Full height in canvas-internal pixels (after scale). */
  h: number;
  /** Rotation in degrees (matches transform.rotation). */
  rotationDeg: number;
}
